import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Package, Truck, CheckCircle, Clock, Search, Loader2,
  User, Phone, MapPin, Camera, AlertTriangle, X, Filter, Scale,
  Percent, Clock3, Plus, CreditCard, XCircle, IndianRupee, Tag,
  ScanSearch, Cog, Sparkles, ShoppingBag, Gift, ArrowRight, Users, Download
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Handles both "YYYY-MM-DD" string and full ISO timestamp
const formatPickupDate = (d) => {
  if (!d) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
    return format(new Date(d + 'T00:00:00'), 'dd MMM yyyy');
  }
  try { return format(new Date(d), 'dd MMM yyyy'); } catch { return d; }
};

// STATUS — what the order is in (display only)
const statusConfig = {
  pending:          { label: 'Pending',              color: 'bg-amber-100  text-amber-700',   icon: Clock },
  pickup_assigned:  { label: 'Pickup In Progress',   color: 'bg-blue-100   text-blue-700',    icon: Truck },
  pickup_done:      { label: 'Pickup Done',           color: 'bg-indigo-100 text-indigo-700',  icon: Package },
  in_process:       { label: 'Inspection In Progress', color: 'bg-violet-100 text-violet-700',  icon: Cog },
  process_complete: { label: 'Inspection Complete',   color: 'bg-teal-100   text-teal-700',    icon: CheckCircle },
  cleaned:          { label: 'Cleaned',               color: 'bg-emerald-100 text-emerald-700',icon: Sparkles },
  out_for_delivery: { label: 'Out for Delivery',      color: 'bg-orange-100 text-orange-700',  icon: Truck },
  delivered:        { label: 'Delivered',             color: 'bg-green-100  text-green-700',   icon: CheckCircle },
  delivery_overdue: { label: 'Overdue',               color: 'bg-red-100    text-red-700',     icon: AlertTriangle },
  cancelled:        { label: 'Cancelled',             color: 'bg-red-100    text-red-700',     icon: XCircle },
};

const STATUS_FLOW = ['pending', 'pickup_assigned', 'pickup_done', 'in_process', 'process_complete', 'cleaned', 'out_for_delivery', 'delivered'];

// ── Service Categories for Create Order modal ─────────────────────────────────
const SERVICE_CATEGORIES = [
  { value: 'flexfold',     label: 'FlexFold (Wash+Iron)',  unit: 'kg',    defaultPrice: 79 },
  { value: 'flashfold',    label: 'FlashFold (Express)',   unit: 'kg',    defaultPrice: 99 },
  { value: 'justpress',    label: 'JustPress (Iron only)', unit: 'kg',    defaultPrice: 49 },
  { value: 'dry_cleaning', label: 'Dry Cleaning',          unit: 'piece', defaultPrice: 150 },
  { value: 'shoe_cleaning',label: 'Shoe Care',             unit: 'piece', defaultPrice: 200 },
  { value: 'home_items',   label: 'Home Linen',            unit: 'piece', defaultPrice: 100 },
];

// ── Item Catalogs ──────────────────────────────────────────────────────────────
const DRY_CLEAN_ITEMS = [
  // Women
  { group: "Women", name: 'Kurta / Kameez', price: 99 }, { group: "Women", name: 'Salwaar', price: 89 },
  { group: "Women", name: 'Saree light', price: 149 }, { group: "Women", name: 'Saree heavy', price: 249 },
  { group: "Women", name: 'Saree cotton', price: 149 }, { group: "Women", name: 'Saree silk', price: 199 },
  { group: "Women", name: 'Saree designer', price: 299 }, { group: "Women", name: 'Saree plain', price: 149 },
  { group: "Women", name: 'Lehenga Normal', price: 349 }, { group: "Women", name: 'Lehenga Medium', price: 299 },
  { group: "Women", name: 'Lehenga Heavy', price: 399 }, { group: "Women", name: 'Lehenga designer', price: 599 },
  { group: "Women", name: 'Dress', price: 199 }, { group: "Women", name: 'Dress Cotton (Indo-western)', price: 199 },
  { group: "Women", name: 'Dress heavy (Indo-western)', price: 349 }, { group: "Women", name: 'Blouse Normal', price: 49 },
  { group: "Women", name: 'Blouse designer', price: 99 }, { group: "Women", name: 'Choli, Lehenga, Dupatta', price: 599 },
  { group: "Women", name: 'Coat long', price: 199 }, { group: "Women", name: 'Coat short', price: 149 },
  { group: "Women", name: 'Dupatta normal', price: 49 }, { group: "Women", name: 'Dupatta heavy', price: 99 },
  { group: "Women", name: 'Formal gown normal', price: 199 }, { group: "Women", name: 'Formal gown heavy', price: 249 },
  { group: "Women", name: 'Gaghra 3pc normal', price: 499 }, { group: "Women", name: 'Gaghra 3pc designer', price: 699 },
  { group: "Women", name: 'Innerwear / undergarments', price: 49 }, { group: "Women", name: 'Kurta + Salwar', price: 149 },
  { group: "Women", name: 'Ladies suit 2pcs', price: 249 }, { group: "Women", name: 'Ladies suit 3pcs', price: 299 },
  { group: "Women", name: 'Night dress', price: 79 }, { group: "Women", name: 'Plazo pant', price: 99 },
  { group: "Women", name: 'Petticoat cotton', price: 79 }, { group: "Women", name: 'Petticoat woolen', price: 99 },
  // Men
  { group: "Men", name: 'Shirt / T-Shirt', price: 99 }, { group: "Men", name: 'Trouser / Jeans', price: 99 },
  { group: "Men", name: 'Suit 2pcs', price: 349 }, { group: "Men", name: 'Suit 3pcs', price: 449 },
  { group: "Men", name: 'Jacket', price: 249 }, { group: "Men", name: 'Blazer long', price: 299 },
  { group: "Men", name: 'Blazer short', price: 249 }, { group: "Men", name: 'Shirt-Pant Combo', price: 129 },
  { group: "Men", name: 'Jacket faux fur long', price: 699 }, { group: "Men", name: 'Jacket faux fur short', price: 599 },
  { group: "Men", name: 'Jacket puffer long', price: 299 }, { group: "Men", name: 'Jacket puffer short', price: 249 },
  { group: "Men", name: 'Jacket normal long', price: 299 }, { group: "Men", name: 'Jacket normal short', price: 249 },
  { group: "Men", name: 'Kurta heavy', price: 149 }, { group: "Men", name: 'Kurta silk', price: 149 },
  { group: "Men", name: 'Kurta pajama light', price: 199 }, { group: "Men", name: 'Kurta pajama heavy', price: 299 },
  { group: "Men", name: 'Sweater full sleeve', price: 149 }, { group: "Men", name: 'Sweater sleeveless', price: 99 },
  { group: "Men", name: 'Sweat shirt', price: 149 },
  // Kids
  { group: "Kids", name: 'Top', price: 49 }, { group: "Kids", name: 'Choli lehenga', price: 299 },
  { group: "Kids", name: 'Coat', price: 149 }, { group: "Kids", name: 'Combo (shirt-pant)', price: 79 },
  { group: "Kids", name: 'Frock cotton', price: 109 }, { group: "Kids", name: 'Frock designer', price: 199 },
  { group: "Kids", name: 'Normal dress', price: 109 }, { group: "Kids", name: 'Normal dress heavy', price: 149 },
  { group: "Kids", name: 'Kurta 2pcs', price: 169 }, { group: "Kids", name: 'Frock', price: 119 },
];

const SHOE_ITEMS = [
  { name: 'Sports Shoes', price: 199 },
  { name: 'Sneaker Shoes', price: 199 },
  { name: 'Leather Shoes', price: 349 },
  { name: 'Suede Shoes', price: 349 },
];

const HOME_ITEMS = [
  { name: 'Blanket Single', price: 299, unit: 'piece' },
  { name: 'Blanket Double', price: 399, unit: 'piece' },
  { name: 'Blanket Divet (Single)', price: 599, unit: 'piece' },
  { name: 'Blanket Divet (Double)', price: 799, unit: 'piece' },
  { name: 'Quilt (Single)', price: 399, unit: 'piece' },
  { name: 'Quilt (Double)', price: 499, unit: 'piece' },
  { name: 'Curtain', price: 199, unit: 'panel' },
  { name: 'Carpet', price: 35, unit: 'sqft' },
];

const CATALOG_ITEMS = { dry_cleaning: DRY_CLEAN_ITEMS, shoe_cleaning: SHOE_ITEMS, home_items: HOME_ITEMS };
const CATALOG_CATEGORIES = new Set(['dry_cleaning', 'shoe_cleaning', 'home_items']);

// Same 1-hour slots as customer IndividualNewOrder page
const PICKUP_TIME_SLOTS = [
  '09:00 – 10:00', '10:00 – 11:00', '11:00 – 12:00', '12:00 – 13:00',
  '13:00 – 14:00', '14:00 – 15:00', '15:00 – 16:00', '16:00 – 17:00',
  '17:00 – 18:00', '18:00 – 19:00', '19:00 – 20:00',
];

// Compute default delivery date = pickup_date + max service timeline days (fallback 4)
const getDeliveryDefaultDate = (order) => {
  const pickupStr = order?.pickup_date;
  let maxDays = 4;
  if (order?.service_timelines?.length) {
    maxDays = Math.max(...order.service_timelines.map(t => t.days || 4));
  }
  const base = pickupStr
    ? new Date(pickupStr.length === 10 ? pickupStr + 'T00:00:00' : pickupStr)
    : new Date();
  base.setDate(base.getDate() + maxDays);
  return base.toISOString().split('T')[0];
};

// ── Create Order Modal ─────────────────────────────────────────────────────────
const CreateOrderModal = ({ show, onClose, token, onSuccess, pgs = [], serviceCategories = SERVICE_CATEGORIES, catalogItems = CATALOG_ITEMS }) => {
  const [customers, setCustomers] = useState([]);
  const [custLoading, setCustLoading] = useState(false);
  const [custSearch, setCustSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const defaultFirstItem = () => {
    const sc = serviceCategories[0];
    return { category: sc.value, description: sc.label, quantity: 2, unit: sc.unit, price_per_unit: sc.defaultPrice, total: 2 * sc.defaultPrice, catalog_item: '' };
  };
  const [lineItems, setLineItems] = useState(() => [defaultFirstItem()]);
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [clothesReceived, setClothesReceived] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // New customer inline creation
  const [showNewCust, setShowNewCust] = useState(false);
  const [newCust, setNewCust] = useState({ full_name: '', email: '', phone: '', password: '' });
  const [creatingCust, setCreatingCust] = useState(false);

  useEffect(() => {
    if (!show) return;
    setCustLoading(true);
    axios.get(`${API}/admin/individual-customers/list`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setCustomers(r.data))
      .catch(() => toast.error('Failed to load customers'))
      .finally(() => setCustLoading(false));
    // reset
    setCustSearch(''); setSelectedCustomer(null); setShowNewCust(false);
    setNewCust({ full_name: '', email: '', phone: '', password: '' });
    const sc = serviceCategories[0];
    setLineItems([{ category: sc.value, description: sc.label, quantity: 2, unit: sc.unit, price_per_unit: sc.defaultPrice, total: 2 * sc.defaultPrice, catalog_item: '' }]);
  }, [show]);

  const filteredCustomers = customers.filter(c =>
    custSearch === '' ||
    (c.full_name || '').toLowerCase().includes(custSearch.toLowerCase()) ||
    (c.phone || '').includes(custSearch)
  );

  const handleCreateCustomer = async () => {
    if (!newCust.full_name || !newCust.phone) {
      toast.error('Name and phone are required'); return;
    }
    setCreatingCust(true);
    try {
      const payload = { full_name: newCust.full_name, phone: newCust.phone };
      if (newCust.email) payload.email = newCust.email;
      const res = await axios.post(`${API}/admin/create-individual-customer`, payload,
        { headers: { Authorization: `Bearer ${token}` } });
      setCustomers(prev => [res.data, ...prev]);
      setSelectedCustomer(res.data);
      setShowNewCust(false);
      toast.success(`Customer "${res.data.full_name}" created and selected`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create customer');
    } finally { setCreatingCust(false); }
  };

  const updateLineItem = (i, field, value) => {
    setLineItems(prev => {
      const updated = [...prev];
      updated[i] = { ...updated[i], [field]: value };
      if (field === 'category') {
        const svc = serviceCategories.find(s => s.value === value);
        if (svc) {
          updated[i].unit = svc.unit;
          updated[i].price_per_unit = svc.defaultPrice;
          updated[i].description = svc.label;
          // Reset catalog item selection when switching category
          updated[i].catalog_item = '';
        }
        // Pre-select first catalog item automatically
        if (CATALOG_CATEGORIES.has(value)) {
          const items = catalogItems[value];
          if (items?.length) {
            updated[i].description = items[0].name;
            updated[i].price_per_unit = items[0].price;
            updated[i].unit = items[0].unit || 'piece';
            updated[i].catalog_item = items[0].name;
            updated[i].quantity = 1;
            updated[i].total = items[0].price;
          }
        }
      }
      if (field === 'catalog_item') {
        // User picked an item from catalog dropdown
        const items = catalogItems[updated[i].category] || [];
        const found = items.find(it => it.name === value);
        if (found) {
          updated[i].description = found.name;
          updated[i].price_per_unit = found.price;
          updated[i].unit = found.unit || 'piece';
          updated[i].total = (parseFloat(updated[i].quantity) || 1) * found.price;
        }
      }
      if (field === 'quantity' || field === 'price_per_unit') {
        updated[i].total = (parseFloat(updated[i].quantity) || 0) * (parseFloat(updated[i].price_per_unit) || 0);
      }
      return updated;
    });
  };

  const addLineItem = () => {
    const sc = serviceCategories[0];
    setLineItems(prev => [...prev, { category: sc.value, description: sc.label, quantity: 2, unit: sc.unit, price_per_unit: sc.defaultPrice, total: 2 * sc.defaultPrice, catalog_item: '' }]);
  };
  const removeLineItem = (i) => setLineItems(prev => prev.filter((_, idx) => idx !== i));

  const totalAmount = lineItems.reduce((s, i) => s + (parseFloat(i.total) || 0), 0);

  const handleSubmit = async () => {
    if (!selectedCustomer) { toast.error('Please select a customer'); return; }
    if (lineItems.length === 0) { toast.error('Add at least one item'); return; }
    setSubmitting(true);
    try {
      const cartItems = lineItems.map(item => ({
        category: item.category,
        description: item.description || serviceCategories.find(s => s.value === item.category)?.label || item.category,
        quantity: parseFloat(item.quantity) || 1,
        unit: item.unit,
        price_per_unit: parseFloat(item.price_per_unit) || 0,
        total: parseFloat(item.total) || 0,
        discount_pct: 0,
        discount_amount: 0,
        final_total: parseFloat(item.total) || 0
      }));
      await axios.post(`${API}/admin/individual-orders/create-for-customer`, {
        customer_id: selectedCustomer.id,
        cart_items: cartItems,
        total_amount: totalAmount,
        special_instructions: specialInstructions || null,
        clothes_received: clothesReceived,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Order created for ${selectedCustomer.full_name}`);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create order');
    } finally { setSubmitting(false); }
  };

  if (!show) return null;
  return (
    <Dialog open={show} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl">
        <DialogHeader>
          <DialogTitle className="text-lg font-black text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Create Order for Customer
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-2">
          {/* Customer Selection */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide">Select Customer</label>
              <button
                type="button"
                onClick={() => { setShowNewCust(v => !v); setSelectedCustomer(null); }}
                className={`text-xs font-bold px-3 py-1 rounded-lg border transition-colors ${showNewCust ? 'bg-slate-100 text-slate-600 border-slate-200' : 'bg-orange-50 text-orange-600 border-orange-200 hover:bg-orange-100'}`}
              >
                {showNewCust ? '← Back to search' : '+ New Customer'}
              </button>
            </div>

            {selectedCustomer ? (
              <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-2.5 flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold text-blue-700">{selectedCustomer.full_name}</p>
                  <p className="text-xs text-blue-500">{selectedCustomer.phone} · {selectedCustomer.email || 'Individual'}</p>
                </div>
                <button onClick={() => setSelectedCustomer(null)} className="text-blue-400 hover:text-blue-600">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : showNewCust ? (
              /* ── Create New Individual Customer ── */
              <div className="bg-orange-50 border border-orange-200 rounded-2xl p-4 space-y-3">
                <p className="text-xs font-black text-orange-700 uppercase tracking-widest">New Individual Customer</p>
                <p className="text-[10px] text-orange-600">Default password = phone number</p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: 'full_name', placeholder: 'Full Name *', type: 'text' },
                    { key: 'phone', placeholder: 'Phone * (10 digits)', type: 'text' },
                    { key: 'email', placeholder: 'Email (optional)', type: 'email' },
                  ].map(({ key, placeholder, type }) => (
                    <input key={key} type={type} placeholder={placeholder}
                      value={newCust[key]}
                      onChange={e => setNewCust(p => ({ ...p, [key]: e.target.value }))}
                      className="w-full border border-orange-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:border-orange-400"
                    />
                  ))}
                </div>
                <button onClick={handleCreateCustomer} disabled={creatingCust}
                  className="w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bold py-2.5 rounded-xl text-sm transition-colors">
                  {creatingCust ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  Create & Select Customer
                </button>
              </div>
            ) : (
              /* ── Search existing customers ── */
              <>
                <input
                  type="text"
                  placeholder="Search by name or phone..."
                  value={custSearch}
                  onChange={e => setCustSearch(e.target.value)}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
                />
                <div className="border border-slate-200 rounded-xl overflow-hidden max-h-44 overflow-y-auto">
                  {custLoading ? (
                    <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-blue-600" /></div>
                  ) : filteredCustomers.length === 0 ? (
                    <p className="text-xs text-slate-400 px-4 py-3 text-center">
                      {customers.length === 0 ? 'No customers registered yet' : 'No customers match your search'}
                    </p>
                  ) : filteredCustomers.slice(0, 10).map(c => (
                    <button key={c.id} onClick={() => { setSelectedCustomer(c); setCustSearch(''); }}
                      className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-blue-50 text-left border-b border-slate-100 last:border-0 transition-colors">
                      <div className="w-7 h-7 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 flex-shrink-0">
                        <span className="text-xs text-white font-bold">{c.full_name[0]?.toUpperCase()}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-700 truncate">{c.full_name}</p>
                        <p className="text-xs text-slate-400">{c.phone}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Line Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-bold text-slate-600 uppercase tracking-wide">Order Items</label>
              <button onClick={addLineItem} className="flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700">
                <Plus className="w-3.5 h-3.5" /> Add Item
              </button>
            </div>
            <div className="space-y-2">
              {lineItems.map((item, i) => (
                <div key={i} className="bg-slate-50 rounded-xl p-3 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] text-slate-500 font-semibold">Service</label>
                      <select
                        value={item.category}
                        onChange={e => updateLineItem(i, 'category', e.target.value)}
                        className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs mt-0.5 bg-white focus:outline-none focus:border-blue-400"
                      >
                        {serviceCategories.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] text-slate-500 font-semibold">Item</label>
                      {CATALOG_CATEGORIES.has(item.category) ? (
                        <select
                          value={item.catalog_item || item.description}
                          onChange={e => updateLineItem(i, 'catalog_item', e.target.value)}
                          className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs mt-0.5 bg-white focus:outline-none focus:border-blue-400"
                        >
                          {item.category === 'dry_cleaning' ? (
                            ['Women', 'Men', 'Kids'].map(grp => (
                              <optgroup key={grp} label={grp}>
                                {catalogItems.dry_cleaning.filter(it => it.group === grp).map(it => (
                                  <option key={it.name} value={it.name}>{it.name} — ₹{it.price}</option>
                                ))}
                              </optgroup>
                            ))
                          ) : (catalogItems[item.category] || []).map(it => (
                            <option key={it.name} value={it.name}>{it.name} — ₹{it.price}</option>
                          ))}
                        </select>
                      ) : (
                        <input type="text" value={item.description}
                          onChange={e => updateLineItem(i, 'description', e.target.value)}
                          className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs mt-0.5 focus:outline-none focus:border-blue-400"
                          placeholder="Item description"
                        />
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-2 items-end">
                    <div>
                      <label className="text-[10px] text-slate-500 font-semibold">Qty</label>
                      <input type="number" min="0.5" step="0.5" value={item.quantity}
                        onChange={e => updateLineItem(i, 'quantity', e.target.value)}
                        className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs mt-0.5 focus:outline-none focus:border-blue-400"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-slate-500 font-semibold">Unit</label>
                      <input type="text" value={item.unit} readOnly
                        className="w-full border border-slate-100 rounded-lg px-2 py-1.5 text-xs mt-0.5 bg-slate-100 text-slate-500"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-slate-500 font-semibold">Price/unit ₹</label>
                      <input type="number" min="0" value={item.price_per_unit}
                        onChange={e => updateLineItem(i, 'price_per_unit', e.target.value)}
                        className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs mt-0.5 focus:outline-none focus:border-blue-400"
                      />
                    </div>
                    <div className="flex items-end gap-1.5">
                      <div className="flex-1">
                        <label className="text-[10px] text-slate-500 font-semibold">Total ₹</label>
                        <div className="border border-slate-100 rounded-lg px-2 py-1.5 text-xs mt-0.5 bg-slate-100 font-bold text-slate-700">
                          {Math.round(item.total)}
                        </div>
                      </div>
                      {lineItems.length > 1 && (
                        <button onClick={() => removeLineItem(i)} className="pb-1 text-red-400 hover:text-red-600">
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Special Instructions */}
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1.5 uppercase tracking-wide">Special Instructions</label>
            <textarea
              value={specialInstructions}
              onChange={e => setSpecialInstructions(e.target.value)}
              placeholder="Any special notes..."
              rows={2}
              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm resize-none focus:outline-none focus:border-blue-400"
            />
          </div>

          {/* Clothes received toggle */}
          <div
            onClick={() => setClothesReceived(v => !v)}
            className={`flex items-center justify-between px-4 py-3.5 rounded-2xl border cursor-pointer transition-all ${
              clothesReceived
                ? 'bg-emerald-50 border-emerald-200'
                : 'bg-amber-50 border-amber-200'
            }`}>
            <div>
              <p className={`text-sm font-bold ${clothesReceived ? 'text-emerald-700' : 'text-amber-700'}`}>
                {clothesReceived ? '✓ Clothes already in shop' : '⏳ Pickup needed'}
              </p>
              <p className="text-[11px] text-slate-400 mt-0.5">
                {clothesReceived
                  ? 'Delivery boy brought them in — skip pickup stage'
                  : 'Customer called in — assign pickup boy after creating'}
              </p>
            </div>
            <div className={`w-11 h-6 rounded-full transition-colors relative flex-shrink-0 ${clothesReceived ? 'bg-emerald-500' : 'bg-amber-400'}`}>
              <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${clothesReceived ? 'right-0.5' : 'left-0.5'}`} />
            </div>
          </div>

          {/* Total */}
          <div className="bg-blue-50 border border-blue-200 rounded-2xl px-5 py-4 flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-700">Order Total</span>
            <span className="text-2xl font-black text-blue-700">₹{Math.round(totalAmount)}</span>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <button onClick={onClose} className="px-5 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
          <button onClick={handleSubmit} disabled={submitting || !selectedCustomer}
            className="px-6 py-2 rounded-xl bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Create Order
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────────
const AdminIndividualOrders = () => {
  const { token, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [upcomingOnly, setUpcomingOnly] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [issueResolution, setIssueResolution] = useState('');
  const [kgInput, setKgInput] = useState('');
  const [settingKg, setSettingKg] = useState(false);
  const [piecesInput, setPiecesInput] = useState('');
  const [settingPieces, setSettingPieces] = useState(false);
  const [deletingPhoto, setDeletingPhoto] = useState(null); // index being deleted
  const [updatingPayment, setUpdatingPayment] = useState(false);
  const [discountEditing, setDiscountEditing] = useState(false);
  const [discountInputs, setDiscountInputs] = useState({});
  const [savingDiscount, setSavingDiscount] = useState(false);
  const [timelineEditing, setTimelineEditing] = useState(false);
  const [timelineInputs, setTimelineInputs] = useState({});
  // Order items editing
  const [itemsEditing, setItemsEditing] = useState(false);
  const [editItems, setEditItems] = useState([]);
  const [savingItems, setSavingItems] = useState(false);
  // Separate payment modal
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  // Cancel order
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  // Overall order discount
  const [overallDiscountInput, setOverallDiscountInput] = useState('');
  const [savingOverallDiscount, setSavingOverallDiscount] = useState(false);
  // Tab
  const [activeTab, setActiveTab] = useState('orders'); // 'orders' | 'referrals'
  // Referrals
  const [referrals, setReferrals] = useState([]);
  const [referralStats, setReferralStats] = useState(null);
  const [referralsLoading, setReferralsLoading] = useState(false);
  const [referralFilter, setReferralFilter] = useState('all');
  const fileInputRef = useRef(null);

  // Delivery assignment
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignOrder, setAssignOrder] = useState(null);
  const [assignType, setAssignType] = useState('delivery'); // 'pickup' | 'delivery'
  const [assignSubType, setAssignSubType] = useState(null); // 'express' | 'standard' | null
  const [deliveryBoys, setDeliveryBoys] = useState([]);
  const [loadingBoys, setLoadingBoys] = useState(false);
  const [assignForm, setAssignForm] = useState({ delivery_boy_id: '', scheduled_date: '', scheduled_time: '', notes: '' });
  const [assigning, setAssigning] = useState(false);
  const [updatingSubStatus, setUpdatingSubStatus] = useState(false);
  const [apiRates, setApiRates] = useState(null);
  const [customKgCategories, setCustomKgCategories] = useState([]); // admin-created kg-priced services

  // Derived catalog arrays from API (with fallback to hardcoded)
  const liveServiceCategories = [
    ...(apiRates
      ? SERVICE_CATEGORIES.map(sc => {
          const r = apiRates[sc.value]?.[0];
          return r ? { ...sc, defaultPrice: r.price } : sc;
        })
      : SERVICE_CATEGORIES),
    // Admin-created custom kg-priced services (e.g. Wash & Fold)
    ...customKgCategories.map(c => ({
      value: c.key,
      label: c.label,
      unit: 'kg',
      defaultPrice: apiRates?.[c.key]?.[0]?.price ?? 0,
    })),
  ];

  const liveDryCleanItems = apiRates
    ? (apiRates.dry_cleaning || []).map(r => ({ group: r.item_group, name: r.catalog_item, price: r.price }))
    : DRY_CLEAN_ITEMS;

  const liveShoeItems = apiRates
    ? (apiRates.shoe_cleaning || []).map(r => ({ name: r.catalog_item, price: r.price }))
    : SHOE_ITEMS;

  const liveHomeItems = apiRates
    ? (apiRates.home_items || []).map(r => ({ name: r.catalog_item, price: r.price, unit: r.unit }))
    : HOME_ITEMS;

  const liveCatalogItems = {
    dry_cleaning: liveDryCleanItems,
    shoe_cleaning: liveShoeItems,
    home_items: liveHomeItems,
  };

  useEffect(() => {
    if (!isSuperAdmin()) { navigate('/dashboard'); return; }
    fetchOrders();
    // Fetch dynamic rates (public endpoint, no auth needed)
    axios.get(`${BACKEND_URL}/api/individual/service-rates`)
      .then(r => setApiRates(r.data))
      .catch(() => {});
    axios.get(`${BACKEND_URL}/api/individual/service-categories`)
      .then(r => setCustomKgCategories((r.data || []).filter(c => c.service_mode === 'kg' && !c.is_system && c.is_active)))
      .catch(() => {});
  }, []);

  const fetchOrders = async () => {
    try {
      const res = await axios.get(`${API}/admin/individual-orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(res.data);
    } catch {
      toast.error('Failed to load individual orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchReferrals = async () => {
    setReferralsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/referrals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferrals(res.data.referrals || []);
      setReferralStats(res.data.stats || null);
    } catch {
      toast.error('Failed to load referrals');
    } finally {
      setReferralsLoading(false);
    }
  };

  const printTags = async (order) => {
    try {
      const resp = await axios.get(`${API}/admin/individual-orders/${order.id}/tags`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blobUrl = URL.createObjectURL(resp.data);
      window.open(blobUrl, '_blank');
    } catch {
      toast.error('Failed to generate tags');
    }
  };

  const downloadInvoice = async (order) => {
    try {
      const resp = await axios.get(`${API}/admin/individual-orders/${order.id}/invoice`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blobUrl = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `invoice-${order.invoice_number || order.id.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error('Failed to download invoice');
    }
  };

  const handleStatusUpdate = async (orderId, newStatus, orderPhotos = [], order = null) => {
    if (newStatus === 'process_complete') {
      const totalPieces = (order || selectedOrder)?.total_pieces || 0;
      const photoCount = orderPhotos?.length || 0;
      if (!totalPieces) {
        toast.error('Set the total piece count in Order Details before proceeding');
        return;
      }
      if (photoCount < totalPieces) {
        toast.error(`Upload at least ${totalPieces} photos — ${photoCount} uploaded so far`);
        return;
      }
    }
    setUpdating(true);
    try {
      await axios.patch(`${API}/admin/individual-orders/${orderId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const labels = { pickup_assigned:'Pickup assigned',pickup_done:'Pickup done',in_process:'Inspection In Progress',process_complete:'Inspection Complete',cleaned:'Cleaned',out_for_delivery:'Out for Delivery',delivered:'Delivered' };
      toast.success(labels[newStatus] ? `✓ ${labels[newStatus]}` : 'Status updated');
      fetchOrders();
      if (selectedOrder?.id === orderId) {
        setSelectedOrder(prev => ({ ...prev, status: newStatus }));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length || !selectedOrder) return;

    const totalPieces = selectedOrder.total_pieces || 0;
    const existingCount = selectedOrder.photos?.length || 0;
    const afterUpload = existingCount + files.length;

    // Warn if uploading more than required
    if (totalPieces > 0 && files.length > totalPieces) {
      toast.error(`You selected ${files.length} photos but only ${totalPieces} pieces are required`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setUploading(true);
    const formData = new FormData();
    files.forEach(f => formData.append('photos', f));
    try {
      const res = await axios.post(`${API}/admin/individual-orders/${selectedOrder.id}/photos`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      if (totalPieces > 0) {
        if (afterUpload >= totalPieces) {
          toast.success(`✓ ${files.length} photo${files.length > 1 ? 's' : ''} uploaded — all ${totalPieces} pieces covered!`);
        } else {
          toast.success(`${files.length} photo${files.length > 1 ? 's' : ''} uploaded (${afterUpload}/${totalPieces}) — ${totalPieces - afterUpload} more needed`);
        }
      } else {
        toast.success(`${files.length} photo${files.length > 1 ? 's' : ''} uploaded`);
      }
      if (res.data?.photos) {
        setSelectedOrder(prev => ({ ...prev, photos: res.data.photos }));
      }
      fetchOrders();
    } catch {
      toast.error('Failed to upload photos');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSetKg = async () => {
    const kg = parseFloat(kgInput);
    if (!kg || kg <= 0) { toast.error('Enter a valid kg value'); return; }
    setSettingKg(true);
    try {
      const res = await axios.patch(
        `${API}/admin/individual-orders/${selectedOrder.id}/set-kg`,
        { kg },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message);
      setKgInput('');
      fetchOrders();
      setSelectedOrder(prev => ({ ...prev, actual_kg: kg }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to set kg');
    } finally {
      setSettingKg(false);
    }
  };

  const handleSetPieces = async () => {
    const pieces = parseInt(piecesInput);
    if (!pieces || pieces <= 0) { toast.error('Enter a valid piece count'); return; }
    setSettingPieces(true);
    try {
      const res = await axios.patch(
        `${API}/admin/individual-orders/${selectedOrder.id}/set-pieces`,
        { total_pieces: pieces },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Total pieces set to ${pieces}`);
      setPiecesInput('');
      setSelectedOrder(res.data);
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to set pieces');
    } finally { setSettingPieces(false); }
  };

  const handleDeletePhoto = async (idx) => {
    if (!selectedOrder) return;
    setDeletingPhoto(idx);
    try {
      const res = await axios.delete(
        `${API}/admin/individual-orders/${selectedOrder.id}/photos/${idx}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedOrder(prev => ({ ...prev, photos: res.data.photos }));
      toast.success('Photo removed');
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove photo');
    } finally { setDeletingPhoto(null); }
  };

  const handleUpdatePayment = async (paymentStatus, paymentMethod = 'cash') => {
    if (!selectedOrder) return;
    setUpdatingPayment(true);
    try {
      await axios.patch(
        `${API}/admin/individual-orders/${selectedOrder.id}/payment`,
        { payment_status: paymentStatus, payment_method: paymentMethod },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(paymentStatus === 'paid' ? 'Marked as paid' : 'Marked as unpaid');
      fetchOrders();
      setSelectedOrder(prev => ({
        ...prev,
        payment_status: paymentStatus,
        payment_method: paymentStatus === 'paid' ? paymentMethod : null,
        paid_at: paymentStatus === 'paid' ? new Date().toISOString() : null,
      }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update payment');
    } finally {
      setUpdatingPayment(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!selectedOrder) return;
    setCancelling(true);
    try {
      const res = await axios.patch(`${API}/admin/individual-orders/${selectedOrder.id}/cancel`,
        { reason: cancelReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedOrder(res.data);
      fetchOrders();
      setShowCancelModal(false);
      setCancelReason('');
      toast.success('Order cancelled');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel order');
    } finally {
      setCancelling(false);
    }
  };

  const handleApplyOverallDiscount = async () => {
    if (!selectedOrder) return;
    const pct = parseFloat(overallDiscountInput);
    if (isNaN(pct) || pct < 0 || pct > 100) {
      toast.error('Enter a valid percentage between 0 and 100');
      return;
    }
    setSavingOverallDiscount(true);
    try {
      const res = await axios.patch(
        `${API}/admin/individual-orders/${selectedOrder.id}/overall-discount`,
        { discount_pct: pct },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(pct === 0 ? 'Overall discount removed' : `${pct}% overall discount applied`);
      setSelectedOrder(res.data);
      fetchOrders();
      setOverallDiscountInput('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply discount');
    } finally {
      setSavingOverallDiscount(false);
    }
  };

  const handleSaveDiscounts = async () => {
    if (!selectedOrder) return;
    setSavingDiscount(true);
    try {
      const discounts = Object.entries(discountInputs).map(([idx, pct]) => ({
        item_index: parseInt(idx),
        discount_pct: parseFloat(pct) || 0
      }));
      const res = await axios.patch(
        `${API}/admin/individual-orders/${selectedOrder.id}/set-discount`,
        { discounts },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedOrder(res.data);
      setDiscountEditing(false);
      toast.success('Discounts saved — customer can now see updated prices');
      fetchOrders();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Failed to save discounts';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally { setSavingDiscount(false); }
  };

  const handleSaveTimelines = async () => {
    if (!selectedOrder) return;
    setUpdating(true);
    try {
      const timelines = Object.entries(timelineInputs).map(([svc, days]) => ({
        service: svc,
        days: parseInt(days) || 1
      }));
      const res = await axios.patch(`${API}/admin/individual-orders/${selectedOrder.id}/set-timelines`,
        { timelines }, { headers: { Authorization: `Bearer ${token}` } });
      setSelectedOrder(res.data);
      setTimelineEditing(false);
      toast.success('Timelines updated');
      fetchOrders();
    } catch { toast.error('Failed to update timelines'); }
    finally { setUpdating(false); }
  };

  const handleSaveItems = async () => {
    if (!selectedOrder) return;
    if (editItems.length === 0) { toast.error('Add at least one item'); return; }
    setSavingItems(true);
    try {
      const res = await axios.patch(
        `${API}/admin/individual-orders/${selectedOrder.id}/update-items`,
        { cart_items: editItems },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedOrder(res.data);
      setItemsEditing(false);
      toast.success('Order items updated — customer can see changes');
      fetchOrders();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Failed to save items';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally { setSavingItems(false); }
  };

  const startEditItems = () => {
    if (!selectedOrder?.cart_items) return;
    setEditItems(selectedOrder.cart_items.map(item => ({ ...item })));
    setItemsEditing(true);
  };

  const updateEditItem = (i, field, value) => {
    setEditItems(prev => {
      const updated = [...prev];
      updated[i] = { ...updated[i], [field]: value };
      if (field === 'category') {
        const svc = liveServiceCategories.find(s => s.value === value);
        if (svc) {
          updated[i].unit = svc.unit;
          if (!CATALOG_CATEGORIES.has(value)) {
            updated[i].description = svc.label;
            updated[i].price_per_unit = svc.defaultPrice;
            updated[i].total = Math.round((parseFloat(updated[i].quantity) || 1) * svc.defaultPrice * 100) / 100;
          }
          // auto-pick first catalog item for catalog categories
          if (CATALOG_CATEGORIES.has(value)) {
            const items = liveCatalogItems[value];
            if (items?.length) {
              updated[i].description = items[0].name;
              updated[i].price_per_unit = items[0].price;
              updated[i].unit = items[0].unit || 'piece';
              updated[i].quantity = 1;
              updated[i].total = items[0].price;
              updated[i].catalog_item = items[0].name;
            }
          }
        }
      }
      if (field === 'catalog_item') {
        const items = liveCatalogItems[updated[i].category] || [];
        const found = items.find(it => it.name === value);
        if (found) {
          updated[i].description = found.name;
          updated[i].price_per_unit = found.price;
          updated[i].unit = found.unit || 'piece';
          updated[i].catalog_item = value;
          // recompute total with current qty
          updated[i].total = Math.round((parseFloat(updated[i].quantity) || 1) * found.price * 100) / 100;
        }
      }
      if (field === 'quantity' || field === 'price_per_unit') {
        const qty = parseFloat(field === 'quantity' ? value : updated[i].quantity) || 0;
        const ppu = parseFloat(field === 'price_per_unit' ? value : updated[i].price_per_unit) || 0;
        updated[i].total = Math.round(qty * ppu * 100) / 100;
      }
      return updated;
    });
  };

  const addEditItem = () => {
    const sc = liveServiceCategories[0];
    const price = sc.defaultPrice;
    setEditItems(prev => [...prev, { category: sc.value, description: sc.label, quantity: 2, unit: sc.unit, price_per_unit: price, total: 2 * price, discount_pct: 0, discount_amount: 0, final_total: 2 * price, catalog_item: '' }]);
  };
  const removeEditItem = (i) => setEditItems(prev => prev.filter((_, idx) => idx !== i));

  const fetchDeliveryBoys = async () => {
    setLoadingBoys(true);
    try {
      const res = await axios.get(`${API}/admin/delivery-boys`, { headers: { Authorization: `Bearer ${token}` } });
      setDeliveryBoys(res.data);
    } catch { toast.error('Failed to load delivery staff'); }
    finally { setLoadingBoys(false); }
  };

  const handleAssignDelivery = async () => {
    if (!assignForm.delivery_boy_id) { toast.error('Select a delivery boy'); return; }
    if (!assignForm.scheduled_date) { toast.error('Pick a scheduled date'); return; }
    setAssigning(true);
    try {
      await axios.post(`${API}/admin/delivery-assignments`, {
        delivery_boy_id: assignForm.delivery_boy_id,
        order_id: assignOrder.id,
        order_type: 'individual',
        assignment_type: assignType,
        scheduled_date: assignForm.scheduled_date,
        scheduled_time: assignForm.scheduled_time || null,
        notes: assignForm.notes || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${assignType === 'pickup' ? 'Pickup' : 'Delivery'} assigned!`);
      setShowAssignModal(false);
      setAssignOrder(null);
      setAssignForm({ delivery_boy_id: '', scheduled_date: '', scheduled_time: '', notes: '' });
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign delivery');
    } finally { setAssigning(false); }
  };

  const EXPRESS_FLOW = ['pending', 'pickup', 'delivered'];
  const STANDARD_FLOW = ['pending', 'pickup', 'processed', 'delivered'];

  const getSubNextStatus = (sub) => {
    const flow = sub.type === 'express' ? EXPRESS_FLOW : STANDARD_FLOW;
    const idx = flow.indexOf(sub.status || 'pending');
    return idx >= 0 && idx < flow.length - 1 ? flow[idx + 1] : null;
  };

  const handleSubOrderStatusUpdate = async (orderId, subType, newStatus) => {
    setUpdatingSubStatus(true);
    try {
      const res = await axios.patch(
        `${API}/admin/individual-orders/${orderId}/sub-orders/${subType}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedOrder(res.data);
      fetchOrders();
      toast.success(`${subType === 'express' ? 'Express' : 'Standard'} sub-order → ${newStatus}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update sub-order status');
    } finally { setUpdatingSubStatus(false); }
  };

  const handleSubOrderAssign = async () => {
    if (!assignForm.delivery_boy_id) { toast.error('Select a delivery boy'); return; }
    if (!assignForm.scheduled_date) { toast.error('Pick a scheduled date'); return; }
    setAssigning(true);
    try {
      const res = await axios.patch(
        `${API}/admin/individual-orders/${assignOrder.id}/sub-orders/${assignSubType}/assign`,
        { ...assignForm, assignment_type: assignType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedOrder(res.data);
      toast.success(`${assignType === 'pickup' ? 'Pickup' : 'Delivery'} boy assigned to ${assignSubType} sub-order`);
      setShowAssignModal(false);
      setAssignOrder(null);
      setAssignSubType(null);
      setAssignForm({ delivery_boy_id: '', scheduled_date: '', scheduled_time: '', notes: '' });
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign');
    } finally { setAssigning(false); }
  };

  const handleResolveIssue = async () => {
    if (!issueResolution.trim()) { toast.error('Please enter a resolution'); return; }
    try {
      await axios.patch(`${API}/admin/individual-orders/${selectedOrder.id}/issue`,
        { resolution: issueResolution },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Issue resolved');
      setShowIssueModal(false);
      setIssueResolution('');
      fetchOrders();
    } catch {
      toast.error('Failed to resolve issue');
    }
  };

  const filtered = orders.filter(o => {
    const matchStatus = filterStatus === 'all' || o.status === filterStatus;
    const matchPayment = paymentFilter === 'all' ||
      (paymentFilter === 'paid' ? o.payment_status === 'paid' : o.payment_status !== 'paid');
    const matchUpcoming = !upcomingOnly ||
      (!['delivered', 'cancelled'].includes(o.status) &&
        o.expected_delivery_date && new Date(o.expected_delivery_date) > new Date());
    const matchSearch = !searchTerm ||
      o.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      o.customer_phone?.includes(searchTerm) ||
      o.id?.includes(searchTerm);
    return matchStatus && matchPayment && matchUpcoming && matchSearch;
  });

  const isOverdue = (o) => {
    if (o.status === 'delivered') return false;
    return o.expected_delivery_date ? new Date(o.expected_delivery_date) < new Date() : false;
  };

  const getNextStatus = (current) => {
    const idx = STATUS_FLOW.indexOf(current);
    return idx >= 0 && idx < STATUS_FLOW.length - 1 ? STATUS_FLOW[idx + 1] : null;
  };

  const statusCounts = orders.reduce((acc, o) => {
    acc[o.status] = (acc[o.status] || 0) + 1;
    return acc;
  }, {});
  const totalKgLogged = orders.reduce((s, o) => s + (o.actual_kg || 0), 0);
  const totalClothes = orders.reduce((s, o) => s + (o.total_clothes || 0), 0);
  const kgPending = orders.filter(o => o.plan_id && o.actual_kg == null && o.status !== 'delivered').length;

  return (
    <div className="p-6 space-y-6" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-black text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Individual Orders</h1>
          <p className="text-sm text-slate-500 mt-0.5">Manage orders from individual customers</p>
        </div>
        <button onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors">
          <Plus className="w-4 h-4" /> Create Order
        </button>
      </div>

      {/* Tab switcher */}
      <div className="flex gap-2">
        <button onClick={() => setActiveTab('orders')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${activeTab === 'orders' ? 'bg-blue-600 text-white shadow-sm' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
          <Package className="w-4 h-4" /> Orders
        </button>
        <button onClick={() => { setActiveTab('referrals'); if (!referrals.length && !referralsLoading) fetchReferrals(); }}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${activeTab === 'referrals' ? 'bg-orange-500 text-white shadow-sm' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
          <Gift className="w-4 h-4" /> Referrals
          {referralStats?.total_count > 0 && <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-full ${activeTab === 'referrals' ? 'bg-white/20 text-white' : 'bg-orange-100 text-orange-600'}`}>{referralStats.total_count}</span>}
        </button>
      </div>

      {activeTab === 'referrals' && (
        <ReferralsPanel
          referrals={referrals}
          stats={referralStats}
          loading={referralsLoading}
          filter={referralFilter}
          setFilter={setReferralFilter}
          onRefresh={fetchReferrals}
        />
      )}

      {activeTab === 'orders' && <>

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {[
          { label: 'Total Orders', value: orders.length, sub: `${statusCounts.pending || 0} pending`, color: 'bg-slate-50 text-slate-600', icon: Package },
          { label: 'Active', value: (statusCounts.pickup_assigned || 0) + (statusCounts.pickup_done || 0) + (statusCounts.in_process || 0) + (statusCounts.process_complete || 0) + (statusCounts.cleaned || 0) + (statusCounts.out_for_delivery || 0), sub: `${statusCounts.delivered || 0} delivered`, color: 'bg-blue-50 text-blue-600', icon: Truck },
          { label: 'Total Clothes', value: totalClothes, sub: 'items logged by customers', color: 'bg-violet-50 text-violet-600', icon: Scale },
        ].map(({ label, value, sub, color, icon: Icon }) => (
          <div key={label} className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 ${color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                <Icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xl font-black text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{value}</p>
                <p className="text-xs text-slate-500 font-semibold">{label}</p>
                <p className="text-[10px] text-slate-400">{sub}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* KG Summary banner */}
      <div className="bg-gradient-to-r from-blue-50 to-emerald-50 border border-blue-100 rounded-2xl px-5 py-4 flex items-center gap-6 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
            <Scale className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <p className="text-xl font-black text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{totalKgLogged.toFixed(1)} <span className="text-base font-normal text-slate-400">kg</span></p>
            <p className="text-xs text-slate-500">Total KG weighed & logged</p>
          </div>
        </div>
        <div className="w-px h-10 bg-slate-200 hidden sm:block" />
        {kgPending > 0 && (
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
            <p className="text-sm text-amber-700 font-semibold">{kgPending} FoldPass order{kgPending > 1 ? 's' : ''} awaiting KG entry</p>
          </div>
        )}
        {kgPending === 0 && totalKgLogged > 0 && (
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-emerald-500" />
            <p className="text-sm text-emerald-700 font-semibold">All FoldPass orders have KG logged</p>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search name, phone, order ID..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="pl-9 bg-white border-slate-200 rounded-xl"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40 bg-white border-slate-200 rounded-xl">
            <Filter className="w-3.5 h-3.5 mr-1 text-slate-400" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="pickup_assigned">Pickup In Progress</SelectItem>
            <SelectItem value="pickup_done">Pickup Done</SelectItem>
            <SelectItem value="in_process">Inspection In Progress</SelectItem>
            <SelectItem value="process_complete">Inspection Complete</SelectItem>
            <SelectItem value="cleaned">Cleaned</SelectItem>
            <SelectItem value="out_for_delivery">Out for Delivery</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
            <SelectItem value="delivery_overdue">Overdue</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <Select value={paymentFilter} onValueChange={setPaymentFilter}>
          <SelectTrigger className="w-36 bg-white border-slate-200 rounded-xl">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Payments</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="unpaid">Unpaid</SelectItem>
          </SelectContent>
        </Select>
        <button
          onClick={() => setUpcomingOnly(v => !v)}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold border transition-all ${
            upcomingOnly ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'
          }`}
        >
          <Clock className="w-3.5 h-3.5" /> Upcoming Deliveries
        </button>
        {(filterStatus !== 'all' || paymentFilter !== 'all' || upcomingOnly || searchTerm) && (
          <button
            onClick={() => { setFilterStatus('all'); setPaymentFilter('all'); setUpcomingOnly(false); setSearchTerm(''); }}
            className="text-xs font-semibold text-slate-400 hover:text-slate-600 underline"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Orders Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl py-16 flex flex-col items-center text-center">
          <Package className="w-12 h-12 text-slate-300 mb-3" strokeWidth={1.5} />
          <p className="font-semibold text-slate-500 mb-1">No orders found</p>
          <p className="text-sm text-slate-400">
            {filterStatus !== 'all' || paymentFilter !== 'all' || upcomingOnly
              ? 'Try a different filter combination'
              : 'Individual orders will appear here'}
          </p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-4 py-3 text-xs font-black text-slate-500 uppercase tracking-wider">Customer</th>
                  <th className="text-left px-4 py-3 text-xs font-black text-slate-500 uppercase tracking-wider">Invoice</th>
                  <th className="text-left px-4 py-3 text-xs font-black text-slate-500 uppercase tracking-wider">Order Date</th>
                  <th className="text-left px-4 py-3 text-xs font-black text-slate-500 uppercase tracking-wider">Due / Pickup</th>
                  <th className="text-left px-4 py-3 text-xs font-black text-slate-500 uppercase tracking-wider">Items / KG</th>
                  <th className="text-left px-4 py-3 text-xs font-black text-slate-500 uppercase tracking-wider">Amount</th>
                  <th className="text-left px-4 py-3 text-xs font-black text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-black text-slate-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((order) => {
                  const overdue = isOverdue(order);
                  const effStatus = overdue ? 'delivery_overdue' : order.status;
                  const cfg = statusConfig[effStatus] || statusConfig.pending;
                  const StatusIcon = cfg.icon;
                  const nextStatus = getNextStatus(order.status);

                  return (
                    <tr key={order.id} className={`hover:bg-slate-50/60 transition-colors ${overdue ? 'bg-red-50/30' : ''}`}>

                      {/* Customer */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0 text-xs font-bold text-orange-600">
                            {order.customer_name?.charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-slate-800 truncate max-w-[130px]">{order.customer_name}</p>
                            <p className="text-xs text-slate-400">{order.customer_phone}</p>
                            {order.address_line1 && (
                              <p className="text-[10px] text-slate-400 truncate max-w-[130px]">{order.address_line2 ? `${order.address_line2}, ` : ''}{order.address_line1}</p>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Invoice */}
                      <td className="px-4 py-3">
                        <div className="space-y-1">
                          {order.invoice_number && (
                            <span className="font-mono text-[10px] bg-blue-50 text-blue-600 border border-blue-100 rounded px-1.5 py-0.5 block w-fit">{order.invoice_number}</span>
                          )}
                          {order.is_split && (
                            <span className="text-[10px] bg-orange-50 text-orange-600 border border-orange-100 rounded px-1.5 py-0.5 flex items-center gap-1 w-fit">
                              <Package className="w-2.5 h-2.5" />Split
                            </span>
                          )}
                          {order.photos?.length > 0 && (
                            <span className="text-[10px] text-blue-500 flex items-center gap-1">
                              <Camera className="w-2.5 h-2.5" />{order.photos.length} photos
                            </span>
                          )}
                          {order.issue?.status === 'open' && (
                            <span className="text-[10px] text-amber-600 flex items-center gap-1 font-semibold">
                              <AlertTriangle className="w-2.5 h-2.5" />Issue open
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Order Date */}
                      <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap">
                        {format(new Date(order.order_date), 'dd MMM yyyy')}
                        <p className="text-[10px] text-slate-400">{format(new Date(order.order_date), 'h:mm a')}</p>
                      </td>

                      {/* Due / Pickup slot */}
                      <td className="px-4 py-3">
                        {order.expected_delivery_date && (
                          <p className={`text-xs font-semibold whitespace-nowrap ${overdue ? 'text-red-600' : 'text-orange-600'}`}>
                            {overdue ? '⚠ OVERDUE' : format(new Date(order.expected_delivery_date), 'dd MMM, h:mm a')}
                          </p>
                        )}
                        {order.pickup_date && (
                          <p className="text-[10px] text-blue-600 font-medium mt-0.5">
                            Pickup: {formatPickupDate(order.pickup_date)}{order.pickup_time_slot ? ` @ ${order.pickup_time_slot}` : ''}
                          </p>
                        )}
                      </td>

                      {/* Items / KG */}
                      <td className="px-4 py-3">
                        <div className="space-y-1">
                          {order.total_clothes > 0 && (
                            <span className="text-xs bg-violet-50 text-violet-700 font-semibold px-2 py-0.5 rounded-full block w-fit">{order.total_clothes} items</span>
                          )}
                          {order.actual_kg != null ? (
                            <span className="text-xs bg-emerald-50 text-emerald-700 font-semibold px-2 py-0.5 rounded-full block w-fit">⚖ {order.actual_kg}kg</span>
                          ) : order.plan_id ? (
                            <span className="text-xs bg-amber-50 text-amber-600 font-semibold px-2 py-0.5 rounded-full block w-fit">⚖ KG pending</span>
                          ) : null}
                        </div>
                      </td>

                      {/* Amount */}
                      <td className="px-4 py-3">
                        {order.total_amount > 0 ? (
                          <div>
                            <p className="text-sm font-bold text-slate-800">₹{order.total_amount?.toLocaleString('en-IN')}</p>
                            {order.payment_status === 'paid'
                              ? <span className="text-[10px] bg-emerald-50 text-emerald-700 font-bold px-1.5 py-0.5 rounded-full border border-emerald-200">Paid</span>
                              : <span className="text-[10px] bg-red-50 text-red-600 font-bold px-1.5 py-0.5 rounded-full border border-red-200">Unpaid</span>
                            }
                          </div>
                        ) : <span className="text-xs text-slate-400">—</span>}
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full ${cfg.color}`}>
                          <StatusIcon className="w-3 h-3" />{cfg.label}
                        </span>
                        {order.assignments?.pickup?.delivery_boy_name && order.status !== 'delivered' && (
                          <p className="text-[10px] text-blue-600 font-medium mt-1 flex items-center gap-1">
                            <User className="w-2.5 h-2.5" />↑ {order.assignments.pickup.delivery_boy_name}
                          </p>
                        )}
                        {order.assignments?.delivery?.delivery_boy_name && (
                          <p className="text-[10px] text-emerald-600 font-medium mt-0.5 flex items-center gap-1">
                            <Truck className="w-2.5 h-2.5" />↓ {order.assignments.delivery.delivery_boy_name}
                          </p>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {/* Always: Details + Tags */}
                          <button
                            onClick={() => { setSelectedOrder(order); setShowDetailModal(true); setDiscountEditing(false); setTimelineEditing(false); setItemsEditing(false); }}
                            className="text-xs font-semibold px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-all whitespace-nowrap">
                            Details
                          </button>
                          <button
                            onClick={() => printTags(order)}
                            className="text-xs font-semibold px-3 py-1.5 bg-violet-50 hover:bg-violet-100 text-violet-700 border border-violet-200 rounded-lg transition-all flex items-center gap-1">
                            <Tag className="w-3 h-3" /> Tags
                          </button>
                          <button
                            onClick={() => downloadInvoice(order)}
                            className="text-xs font-semibold px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 rounded-lg transition-all flex items-center gap-1">
                            <Download className="w-3 h-3" /> Invoice
                          </button>
                          <button
                            onClick={() => { setSelectedOrder(order); setShowPaymentModal(true); }}
                            className={`text-xs font-semibold px-3 py-1.5 rounded-lg border transition-all flex items-center gap-1 ${order.payment_status === 'paid' ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100' : 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100'}`}>
                            <IndianRupee className="w-3 h-3" />
                            {order.payment_status === 'paid'
                              ? (order.payment_method === 'upi' ? 'Paid (UPI)' : order.payment_method === 'easebuzz' ? 'Paid (Online)' : 'Paid (Cash)')
                              : 'Payment'}
                          </button>

                          {!order.is_split && (() => {
                            const s = order.status;
                            return (<>
                              {/* PENDING → assign pickup boy (status will auto-change to pickup_assigned) */}
                              {s === 'pending' && (
                                <button onClick={() => {
                                  setAssignOrder(order); setAssignType('pickup'); setAssignSubType(null);
                                  setAssignForm({ delivery_boy_id: '', scheduled_date: order.pickup_date || new Date().toISOString().split('T')[0], scheduled_time: order.pickup_time_slot || '', notes: '' });
                                  fetchDeliveryBoys(); setShowAssignModal(true);
                                }} className="text-xs font-semibold px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-all flex items-center gap-1">
                                  <Truck className="w-3 h-3" /> Assign Pickup
                                </button>
                              )}

                              {/* PICKUP_ASSIGNED → waiting for pickup boy, no admin action needed */}
                              {s === 'pickup_assigned' && (
                                <span className="text-[11px] text-blue-500 font-medium italic">Waiting for pickup...</span>
                              )}

                              {/* PICKUP_DONE → admin marks In Process */}
                              {s === 'pickup_done' && (
                                <button onClick={() => handleStatusUpdate(order.id, 'in_process', order.photos, order)}
                                  disabled={updating}
                                  className="text-xs font-semibold px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-all disabled:opacity-60 flex items-center gap-1">
                                  <Cog className="w-3 h-3" /> Mark Inspection In Progress
                                </button>
                              )}

                              {/* IN_PROCESS → set pieces + upload photos, then Process Complete */}
                              {s === 'in_process' && (() => {
                                const pieces = order.total_pieces || 0;
                                const photoCount = order.photos?.length || 0;
                                const piecesSet = pieces > 0;
                                const photosOk = piecesSet && photoCount >= pieces;
                                return (
                                  <button onClick={() => { setSelectedOrder(order); setShowDetailModal(true); setItemsEditing(false); }}
                                    className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-all flex items-center gap-1 ${
                                      photosOk
                                        ? 'bg-teal-600 hover:bg-teal-700 text-white'
                                        : 'bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200'
                                    }`}>
                                    {photosOk
                                      ? <><CheckCircle className="w-3 h-3" /> Inspection Complete</>
                                      : !piecesSet
                                        ? <><Camera className="w-3 h-3" /> Set Pieces & Photos</>
                                        : <><Camera className="w-3 h-3" /> {photoCount}/{pieces} Photos</>
                                    }
                                  </button>
                                );
                              })()}

                              {/* PROCESS_COMPLETE → mark cleaned */}
                              {s === 'process_complete' && (
                                <button onClick={() => handleStatusUpdate(order.id, 'cleaned', order.photos, order)}
                                  disabled={updating}
                                  className="text-xs font-semibold px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-all disabled:opacity-60 flex items-center gap-1">
                                  <Sparkles className="w-3 h-3" /> Mark Cleaned
                                </button>
                              )}

                              {/* CLEANED → assign delivery boy (status auto-changes to out_for_delivery) */}
                              {s === 'cleaned' && (
                                <button onClick={() => {
                                  setAssignOrder(order); setAssignType('delivery'); setAssignSubType(null);
                                  setAssignForm({ delivery_boy_id: '', scheduled_date: getDeliveryDefaultDate(order), scheduled_time: '', notes: '' });
                                  fetchDeliveryBoys(); setShowAssignModal(true);
                                }} className="text-xs font-semibold px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-all flex items-center gap-1">
                                  <Truck className="w-3 h-3" /> Assign Delivery
                                </button>
                              )}

                              {/* OUT_FOR_DELIVERY → delivery boy marks done; fallback manual */}
                              {s === 'out_for_delivery' && (
                                <button onClick={() => handleStatusUpdate(order.id, 'delivered', order.photos)}
                                  disabled={updating}
                                  className="text-xs font-semibold px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-all disabled:opacity-60 flex items-center gap-1">
                                  <CheckCircle className="w-3 h-3" /> Mark Delivered
                                </button>
                              )}
                            </>);
                          })()}

                          {order.issue?.status === 'open' && (
                            <button onClick={() => { setSelectedOrder(order); setShowIssueModal(true); }}
                              className="text-xs font-semibold px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-all">
                              Resolve
                            </button>
                          )}
                          {order.is_split && (
                            <span className="text-[10px] text-orange-600 font-semibold">Manage in Details</span>
                          )}
                          {!['delivered', 'cancelled'].includes(order.status) && (
                            <button onClick={() => { setSelectedOrder(order); setShowCancelModal(true); }}
                              className="text-xs font-semibold px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 rounded-lg transition-all">
                              Cancel
                            </button>
                          )}
                          {order.status === 'cancelled' && (
                            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${order.cancelled_by === 'customer' ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-red-600'}`}>
                              {order.cancelled_by === 'customer' ? 'Cancelled by Customer' : 'Cancelled by Admin'}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2.5 border-t border-slate-100 bg-slate-50 text-xs text-slate-400 font-medium">
            {filtered.length} order{filtered.length !== 1 ? 's' : ''}
            {filterStatus !== 'all' ? ` · status: ${filterStatus}` : ''}
            {paymentFilter !== 'all' ? ` · payment: ${paymentFilter}` : ''}
            {upcomingOnly ? ' · upcoming deliveries only' : ''}
          </div>
        </div>
      )}

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Package className="w-5 h-5 text-orange-500" /> Order Details
            </DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-2xl p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-400">Customer</span>
                  <span className="font-semibold text-slate-800">{selectedOrder.customer_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Phone</span>
                  <span className="text-slate-700">{selectedOrder.customer_phone}</span>
                </div>
                {selectedOrder.customer_email && (
                  <div className="flex justify-between">
                    <span className="text-slate-400">Email</span>
                    <span className="text-slate-700">{selectedOrder.customer_email}</span>
                  </div>
                )}
                {selectedOrder.service_summary && (
                  <div className="flex justify-between">
                    <span className="text-slate-400">Services</span>
                    <span className="font-semibold text-slate-800 text-right max-w-[60%]">{selectedOrder.service_summary}</span>
                  </div>
                )}
                {selectedOrder.plan_id && (
                  <div className="flex justify-between">
                    <span className="text-slate-400">Actual Weight</span>
                    <span className={`font-semibold ${selectedOrder.actual_kg != null ? 'text-emerald-700' : 'text-amber-600'}`}>
                      {selectedOrder.actual_kg != null ? `${selectedOrder.actual_kg}kg` : 'Pending weighing'}
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Status</span>
                  <div className="flex items-center gap-2">
                    <Badge className={`text-xs ${statusConfig[selectedOrder.status]?.color || ''}`}>
                      {statusConfig[selectedOrder.status]?.label || selectedOrder.status}
                    </Badge>
                    {selectedOrder.status === 'cancelled' && selectedOrder.cancelled_by === 'customer' && (
                      <span className="text-[10px] text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">by customer</span>
                    )}
                  </div>
                </div>
                {selectedOrder.status === 'cancelled' && selectedOrder.cancellation_reason && (
                  <div className="flex justify-between">
                    <span className="text-slate-400">Cancel Reason</span>
                    <span className="text-red-600 text-xs text-right max-w-[60%]">{selectedOrder.cancellation_reason}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-slate-400">Order Date</span>
                  <span className="text-slate-700">{format(new Date(selectedOrder.order_date), 'dd MMM yyyy')}</span>
                </div>
                {selectedOrder.invoice_number && (
                  <div className="flex justify-between">
                    <span className="text-slate-400">Invoice No.</span>
                    <span className="font-mono text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded px-2 py-0.5">{selectedOrder.invoice_number}</span>
                  </div>
                )}
                {selectedOrder.pickup_date && (
                  <div className="flex justify-between">
                    <span className="text-slate-400">Pickup Slot</span>
                    <span className="text-blue-700 font-semibold">
                      {formatPickupDate(selectedOrder.pickup_date)}{selectedOrder.pickup_time_slot ? ` @ ${selectedOrder.pickup_time_slot}` : ''}
                    </span>
                  </div>
                )}
                {selectedOrder.expected_delivery_date && (
                  <div className="flex justify-between">
                    <span className="text-slate-400">Expected Delivery (48h)</span>
                    <span className="text-orange-600 font-medium">{format(new Date(selectedOrder.expected_delivery_date), 'dd MMM, h:mm a')}</span>
                  </div>
                )}
                {(selectedOrder.address_line1 || selectedOrder.address_line2) && (
                  <div className="pt-2 border-t border-slate-200">
                    <p className="text-slate-400 text-xs mb-1">Delivery Address</p>
                    <p className="text-slate-700 text-sm">
                      {selectedOrder.address_line2 ? `${selectedOrder.address_line2}, ` : ''}{selectedOrder.address_line1}
                    </p>
                  </div>
                )}
                {selectedOrder.special_instructions && (
                  <div className="pt-2 border-t border-slate-200">
                    <p className="text-slate-400 text-xs mb-1">Special Instructions</p>
                    <p className="text-slate-700">{selectedOrder.special_instructions}</p>
                  </div>
                )}
              </div>

              {/* Status update */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <p className="text-sm font-bold text-slate-700">Update Status</p>
                  {(() => {
                    const pieces = selectedOrder?.total_pieces || 0;
                    const photos = selectedOrder?.photos?.length || 0;
                    if (!pieces) return (
                      <span className="text-[10px] bg-red-100 text-red-600 font-semibold px-2 py-0.5 rounded-full flex items-center gap-1">
                        <Camera className="w-2.5 h-2.5" /> Set piece count first
                      </span>
                    );
                    if (photos < pieces) return (
                      <span className="text-[10px] bg-amber-100 text-amber-700 font-semibold px-2 py-0.5 rounded-full flex items-center gap-1">
                        <Camera className="w-2.5 h-2.5" /> {photos}/{pieces} photos — need {pieces - photos} more
                      </span>
                    );
                    return null;
                  })()}
                </div>
                <div className="flex flex-wrap gap-2">
                  {STATUS_FLOW.map(s => (
                    <button key={s}
                      onClick={() => handleStatusUpdate(selectedOrder.id, s, selectedOrder.photos)}
                      disabled={updating || s === selectedOrder.status}
                      className={`text-xs font-semibold px-3 py-2 rounded-xl transition-all disabled:opacity-50 ${
                        s === selectedOrder.status
                          ? 'bg-blue-600 text-white cursor-default'
                          : 'bg-slate-100 hover:bg-blue-50 text-slate-700 hover:text-blue-700'
                      }`}>
                      {statusConfig[s]?.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* FoldPass KG entry */}
              {selectedOrder.plan_id && (
                <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Scale className="w-4 h-4 text-blue-600" />
                    <p className="text-sm font-bold text-blue-800">FoldPass — Log Actual Weight</p>
                    {selectedOrder.actual_kg != null && (
                      <span className="ml-auto text-xs font-bold bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                        {selectedOrder.actual_kg}kg logged
                      </span>
                    )}
                  </div>
                  {/* Plan balance + expiry */}
                  {selectedOrder.plan_info && (() => {
                    const p = selectedOrder.plan_info;
                    const kgLeft = Math.max(0, (p.total_kg || 0) - (p.used_kg || 0));
                    const expiry = p.expiry_date ? new Date(p.expiry_date) : null;
                    const daysLeft = expiry ? Math.max(0, Math.ceil((expiry - new Date()) / (1000 * 60 * 60 * 24))) : 0;
                    const pct = p.total_kg > 0 ? Math.round((kgLeft / p.total_kg) * 100) : 0;
                    return (
                      <div className="mb-3 bg-white rounded-xl p-3 border border-blue-100 space-y-2">
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-bold text-slate-700">{p.plan_name || p.plan_type}</span>
                          <span className={`font-bold px-2 py-0.5 rounded-full text-[10px] ${daysLeft <= 7 ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                            {daysLeft}d left — expires {expiry ? expiry.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full transition-all ${pct > 60 ? 'bg-emerald-500' : pct > 30 ? 'bg-orange-400' : 'bg-red-500'}`} style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-xs font-bold text-slate-700 whitespace-nowrap">{kgLeft.toFixed(1)} / {p.total_kg} kg</span>
                        </div>
                      </div>
                    );
                  })()}
                  {selectedOrder.actual_kg != null && (
                    <p className="text-xs text-slate-500 mb-2">Previously logged: <strong>{selectedOrder.actual_kg}kg</strong>. Enter new value to update.</p>
                  )}
                  <div className="flex gap-2">
                    <input
                      type="number"
                      min="0.1"
                      step="0.1"
                      value={kgInput}
                      onChange={e => setKgInput(e.target.value)}
                      placeholder="e.g. 3.5"
                      className="flex-1 px-3 py-2 bg-white border border-blue-200 rounded-xl text-sm font-bold text-slate-800 outline-none focus:border-blue-500 transition-colors"
                    />
                    <span className="self-center text-sm text-slate-500 font-medium">kg</span>
                    <button
                      onClick={handleSetKg}
                      disabled={settingKg || !kgInput}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold rounded-xl transition-all disabled:opacity-60 flex items-center gap-1.5">
                      {settingKg ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Scale className="w-3.5 h-3.5" />}
                      Save
                    </button>
                  </div>
                </div>
              )}

              {/* Pickup Summary — shown when simplified checkout was used */}
              {selectedOrder?.cart_items?.some(i => i.is_simplified) && (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                      <Package className="w-4 h-4 text-amber-600" />
                    </div>
                    <div>
                      <span className="text-sm font-bold text-amber-800">Pickup Summary (from delivery boy)</span>
                      <p className="text-[10px] text-amber-600 mt-0.5">Use "Edit Items / Prices" below to add full item breakdown</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {selectedOrder.cart_items.filter(i => i.is_simplified).map((item, i) => (
                      <div key={i} className={`flex items-center justify-between px-3 py-2 rounded-xl text-xs ${item.pending_breakdown ? 'bg-amber-100 border border-amber-200' : 'bg-white border border-amber-100'}`}>
                        <div>
                          <span className="font-semibold text-slate-700">{item.description}</span>
                          {item.total_pcs > 0 && <span className="ml-2 text-slate-500">· {item.total_pcs} pcs recorded</span>}
                        </div>
                        {item.pending_breakdown ? (
                          <span className="text-amber-600 font-bold bg-amber-200 px-2 py-0.5 rounded-full text-[10px]">Needs breakdown</span>
                        ) : (
                          <span className="text-emerald-700 font-bold">₹{item.total}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Order Items Editor */}
              {selectedOrder?.cart_items?.length > 0 && (
                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                        <ShoppingBag className="w-4 h-4 text-blue-600" />
                      </div>
                      <span className="text-sm font-bold text-slate-700">
                        Order Items
                        {selectedOrder.cart_items.some(i => i.pending_breakdown) && (
                          <span className="ml-2 text-[10px] font-bold text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full">Breakdown needed</span>
                        )}
                      </span>
                    </div>
                    {!itemsEditing ? (
                      <button onClick={startEditItems}
                        className={`text-xs font-semibold px-3 py-1.5 rounded-lg border ${selectedOrder.cart_items.some(i => i.pending_breakdown) ? 'text-amber-700 bg-amber-50 border-amber-300 animate-pulse' : 'text-blue-600 hover:text-blue-700 bg-blue-50 border-blue-200'}`}>
                        {selectedOrder.cart_items.some(i => i.pending_breakdown) ? '⚠ Fill Item Breakdown' : 'Edit Items / Prices'}
                      </button>
                    ) : (
                      <div className="flex gap-2">
                        <button onClick={() => setItemsEditing(false)} className="text-xs text-slate-500 px-2 py-1 rounded-lg hover:bg-slate-100">Cancel</button>
                        <button onClick={handleSaveItems} disabled={savingItems}
                          className="text-xs font-bold text-white bg-blue-600 px-3 py-1.5 rounded-lg flex items-center gap-1 disabled:opacity-60">
                          {savingItems && <Loader2 className="w-3 h-3 animate-spin" />}
                          Save Changes
                        </button>
                      </div>
                    )}
                  </div>

                  {itemsEditing ? (
                    /* ── EDIT MODE ── */
                    <div className="space-y-2">
                      {editItems.some(it => it.pending_breakdown) && (
                        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-xs text-amber-700">
                          <span className="font-bold mt-0.5">⚠</span>
                          <span>Items marked below need a proper breakdown. Change the service/item and set the correct quantity and price for each.</span>
                        </div>
                      )}
                      {editItems.map((item, i) => (
                        <div key={i} className={`rounded-xl p-3 border space-y-2 ${item.pending_breakdown ? 'bg-amber-50 border-amber-300' : 'bg-white border-slate-200'}`}>
                          {item.pending_breakdown && (
                            <div className="flex items-center gap-1.5 text-[10px] text-amber-700 font-bold">
                              <span>⚠ Needs breakdown</span>
                              <span className="text-amber-500 font-normal">· Delivery boy recorded: {item.total_pcs} pcs</span>
                            </div>
                          )}
                          {/* Row 1: service + item */}
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="text-[10px] text-slate-400 font-semibold uppercase">Service</label>
                              <select value={item.category}
                                onChange={e => updateEditItem(i, 'category', e.target.value)}
                                className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs mt-0.5 bg-slate-50 focus:outline-none focus:border-blue-400">
                                {liveServiceCategories.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                              </select>
                            </div>
                            <div>
                              <label className="text-[10px] text-slate-400 font-semibold uppercase">Item</label>
                              {CATALOG_CATEGORIES.has(item.category) ? (
                                <select value={item.catalog_item || item.description}
                                  onChange={e => updateEditItem(i, 'catalog_item', e.target.value)}
                                  className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs mt-0.5 bg-slate-50 focus:outline-none focus:border-blue-400">
                                  {item.category === 'dry_cleaning' ? (
                                    ['Women', 'Men', 'Kids'].map(grp => (
                                      <optgroup key={grp} label={grp}>
                                        {liveDryCleanItems.filter(it => it.group === grp).map(it => (
                                          <option key={it.name} value={it.name}>{it.name} — ₹{it.price}</option>
                                        ))}
                                      </optgroup>
                                    ))
                                  ) : (liveCatalogItems[item.category] || []).map(it => (
                                    <option key={it.name} value={it.name}>{it.name} — ₹{it.price}</option>
                                  ))}
                                </select>
                              ) : (
                                <input type="text" value={item.description}
                                  onChange={e => updateEditItem(i, 'description', e.target.value)}
                                  className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs mt-0.5 focus:outline-none focus:border-blue-400 bg-slate-50"
                                  placeholder="Item description" />
                              )}
                            </div>
                          </div>
                          {/* Row 2: qty, price, discount, total, delete */}
                          <div className="grid grid-cols-5 gap-1.5 items-end">
                            <div>
                              <label className="text-[10px] text-slate-400 font-semibold uppercase">Qty</label>
                              <input type="number" min="0.5" step="0.5" value={item.quantity}
                                onChange={e => updateEditItem(i, 'quantity', e.target.value)}
                                className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs mt-0.5 focus:outline-none focus:border-blue-400 bg-slate-50" />
                            </div>
                            <div>
                              <label className="text-[10px] text-slate-400 font-semibold uppercase">Price/unit ₹</label>
                              <input type="number" min="0" value={item.price_per_unit}
                                onChange={e => updateEditItem(i, 'price_per_unit', e.target.value)}
                                className="w-full border border-blue-200 rounded-lg px-2 py-1.5 text-xs mt-0.5 focus:outline-none focus:border-blue-500 bg-blue-50 font-semibold" />
                            </div>
                            <div>
                              <label className="text-[10px] text-slate-400 font-semibold uppercase">Disc %</label>
                              <input type="number" min="0" max="100" step="1" value={item.discount_pct || 0}
                                onChange={e => updateEditItem(i, 'discount_pct', parseFloat(e.target.value) || 0)}
                                className="w-full border border-orange-200 rounded-lg px-2 py-1.5 text-xs mt-0.5 focus:outline-none focus:border-orange-400 bg-orange-50" />
                            </div>
                            <div>
                              <label className="text-[10px] text-slate-400 font-semibold uppercase">Total ₹</label>
                              <div className="border border-slate-100 rounded-lg px-2 py-1.5 text-xs mt-0.5 bg-slate-100 font-bold text-slate-700">
                                {Math.round(item.total)}
                              </div>
                            </div>
                            <div className="flex items-end pb-0.5">
                              {editItems.length > 1 && (
                                <button onClick={() => removeEditItem(i)} className="w-full flex justify-center text-red-400 hover:text-red-600">
                                  <X className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      <button onClick={addEditItem}
                        className="w-full flex items-center justify-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-700 py-2 border border-dashed border-blue-300 rounded-xl hover:bg-blue-50 transition-colors">
                        <Plus className="w-3.5 h-3.5" /> Add Item
                      </button>
                      <div className="flex justify-between items-center px-1 pt-1 border-t border-slate-200 mt-2">
                        <span className="text-xs text-slate-500">Order Total (excl. GST/delivery)</span>
                        <span className="text-sm font-black text-blue-700">₹{editItems.reduce((s, it) => s + (parseFloat(it.total) || 0), 0).toLocaleString('en-IN')}</span>
                      </div>
                    </div>
                  ) : (
                    /* ── READ MODE ── */
                    <div className="space-y-1.5">
                      {selectedOrder.cart_items.map((item, i) => (
                        <div key={i} className="flex items-center justify-between text-xs py-1.5 border-b border-slate-100 last:border-0">
                          <div className="flex-1 min-w-0 mr-3">
                            <span className="font-medium text-slate-700 truncate block">{item.description}</span>
                            <span className="text-slate-400">{item.quantity} {item.unit} × ₹{item.price_per_unit}</span>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            {(item.discount_pct || 0) > 0 ? (
                              <>
                                <span className="line-through text-slate-400">₹{item.total}</span>
                                <span className="text-orange-600 font-bold">-{item.discount_pct}%</span>
                                <span className="text-emerald-600 font-bold">₹{item.final_total ?? item.total}</span>
                              </>
                            ) : (
                              <span className="font-bold text-slate-700">₹{item.total}</span>
                            )}
                          </div>
                        </div>
                      ))}
                      <div className="flex justify-between items-center pt-2 mt-1">
                        <span className="text-xs text-slate-400">Subtotal</span>
                        <span className="text-sm font-bold text-slate-700">₹{selectedOrder.subtotal?.toFixed(2) ?? selectedOrder.total_amount?.toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Service Timelines */}
              {selectedOrder?.service_timelines?.length > 0 && (
                <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Clock3 className="w-4 h-4 text-blue-600" />
                      </div>
                      <span className="text-sm font-bold text-slate-700">Delivery Timelines</span>
                    </div>
                    {!timelineEditing ? (
                      <button onClick={() => {
                        const init = {};
                        selectedOrder.service_timelines.forEach(t => { init[t.service] = String(t.days); });
                        setTimelineInputs(init);
                        setTimelineEditing(true);
                      }} className="text-xs font-semibold text-blue-600 hover:text-blue-700">Edit</button>
                    ) : (
                      <div className="flex gap-2">
                        <button onClick={() => setTimelineEditing(false)} className="text-xs text-slate-500">Cancel</button>
                        <button onClick={handleSaveTimelines} disabled={updating} className="text-xs font-bold text-white bg-blue-600 px-3 py-1 rounded-lg">Save</button>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    {selectedOrder.service_timelines.map((tl) => (
                      <div key={tl.service} className="flex items-center justify-between text-xs">
                        <div>
                          <span className="text-slate-700 font-medium">{tl.label}</span>
                          {tl.max_days && tl.max_days > 1 && (
                            <span className="ml-1.5 text-slate-400">(max {tl.max_days}d)</span>
                          )}
                        </div>
                        {timelineEditing ? (
                          <div className="flex items-center gap-1.5">
                            <input
                              type="number" min="1" max={tl.max_days || 30}
                              value={timelineInputs[tl.service] || tl.days}
                              onChange={e => setTimelineInputs(prev => ({ ...prev, [tl.service]: e.target.value }))}
                              className="w-14 border border-blue-200 rounded-lg px-2 py-1 text-center text-xs"
                            />
                            <span className="text-slate-400">days</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="text-blue-600 font-bold">{tl.days} {tl.days === 1 ? 'day' : 'days'}</span>
                            <span className="text-slate-400">by {format(new Date(tl.due_date), 'dd MMM, h:mm a')}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Split Order Sub-orders */}
              {selectedOrder?.is_split && selectedOrder?.sub_orders?.length > 0 && (
                <div className="rounded-2xl border border-orange-200 bg-orange-50 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
                      <Package className="w-4 h-4 text-orange-600" />
                    </div>
                    <span className="text-sm font-bold text-slate-700">Split Order — Manage Sub-orders</span>
                    <span className="ml-auto text-xs bg-orange-100 text-orange-700 px-2.5 py-1 rounded-full font-bold">2 Parts</span>
                  </div>
                  <div className="space-y-3">
                    {selectedOrder.sub_orders.map((sub, idx) => {
                      const isExpress = sub.type === 'express';
                      const nextStatus = getSubNextStatus(sub);
                      // If parent order is past pickup phase but sub-order is still pending/pickup_assigned,
                      // treat as pickup_done (clothes are already in shop — admin-created order scenario)
                      const PAST_PICKUP = ['pickup_done','in_process','process_complete','cleaned','out_for_delivery','delivered'];
                      const rawSubStatus = sub.status || 'pending';
                      const subStatus = (PAST_PICKUP.includes(selectedOrder.status) && ['pending','pickup_assigned'].includes(rawSubStatus))
                        ? 'pickup_done' : rawSubStatus;
                      const subCfg = statusConfig[subStatus] || statusConfig.pending;
                      const SubIcon = subCfg.icon;
                      return (
                        <div key={idx} className={`rounded-xl p-3 border ${isExpress ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'}`}>
                          {/* Header */}
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <span className={`text-xs font-bold ${isExpress ? 'text-amber-700' : 'text-blue-700'}`}>{sub.label}</span>
                              <span className="text-[10px] text-slate-400 ml-2">Due: {format(new Date(sub.expected_delivery_date), 'dd MMM, h:mm a')}</span>
                            </div>
                            <span className={`flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${subCfg.color}`}>
                              <SubIcon className="w-2.5 h-2.5" />{subCfg.label}
                            </span>
                          </div>
                          {/* Services */}
                          <div className="flex flex-wrap gap-1 mb-2.5">
                            {sub.services.map((s, i) => (
                              <span key={i} className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${isExpress ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>{s}</span>
                            ))}
                          </div>
                          {/* Assignment info */}
                          <div className="space-y-1 mb-2.5">
                            {sub.pickup_assignment ? (
                              <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
                                <Package className="w-3 h-3 text-blue-500" />
                                <span className="font-semibold text-blue-600">Pickup:</span>
                                <span>{sub.pickup_assignment.delivery_boy_name} · {sub.pickup_assignment.scheduled_date}</span>
                              </div>
                            ) : subStatus === 'pending' && (
                              <p className="text-[10px] text-amber-600 font-semibold">⚠ No pickup boy assigned</p>
                            )}
                            {sub.delivery_assignment ? (
                              <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
                                <Truck className="w-3 h-3 text-emerald-500" />
                                <span className="font-semibold text-emerald-600">Delivery:</span>
                                <span>{sub.delivery_assignment.delivery_boy_name} · {sub.delivery_assignment.scheduled_date}</span>
                              </div>
                            ) : subStatus !== 'delivered' && subStatus !== 'pending' && (
                              <p className="text-[10px] text-amber-600 font-semibold">⚠ No delivery boy assigned</p>
                            )}
                          </div>
                          {/* Actions */}
                          <div className="flex items-center gap-2 flex-wrap">
                            {nextStatus && (
                              <button
                                onClick={() => {
                                  if (nextStatus === 'pickup' && !sub.pickup_assignment) {
                                    toast.error('Assign a pickup boy first'); return;
                                  }
                                  if (nextStatus === 'delivered' && !selectedOrder.photos?.length) {
                                    toast.error('Upload verification photos before marking as Delivered'); return;
                                  }
                                  if (nextStatus === 'delivered' && !sub.delivery_assignment) {
                                    toast.error('Assign a delivery boy first'); return;
                                  }
                                  handleSubOrderStatusUpdate(selectedOrder.id, sub.type, nextStatus);
                                }}
                                disabled={updatingSubStatus}
                                className={`text-[11px] font-bold px-3 py-1.5 rounded-lg text-white flex items-center gap-1.5 disabled:opacity-50 ${isExpress ? 'bg-amber-500 hover:bg-amber-600' : 'bg-blue-600 hover:bg-blue-700'}`}>
                                {updatingSubStatus ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                                → {nextStatus.charAt(0).toUpperCase() + nextStatus.slice(1)}
                              </button>
                            )}
                            {subStatus === 'pending' && !sub.pickup_assignment && (
                              <button
                                onClick={() => {
                                  setAssignOrder(selectedOrder); setAssignSubType(sub.type); setAssignType('pickup');
                                  setAssignForm({ delivery_boy_id: '', scheduled_date: selectedOrder.pickup_date || new Date().toISOString().split('T')[0], scheduled_time: selectedOrder.pickup_time_slot || '', notes: '' });
                                  fetchDeliveryBoys(); setShowAssignModal(true);
                                }}
                                className="text-[11px] font-bold px-3 py-1.5 rounded-lg bg-blue-500 hover:bg-blue-600 text-white flex items-center gap-1.5">
                                <Package className="w-3 h-3" /> Assign Pickup
                              </button>
                            )}
                            {subStatus !== 'delivered' && subStatus !== 'pending' && !sub.delivery_assignment && (
                              <button
                                onClick={() => {
                                  setAssignOrder(selectedOrder); setAssignSubType(sub.type); setAssignType('delivery');
                                  setAssignForm({ delivery_boy_id: '', scheduled_date: getDeliveryDefaultDate(selectedOrder), scheduled_time: '', notes: '' });
                                  fetchDeliveryBoys(); setShowAssignModal(true);
                                }}
                                className="text-[11px] font-bold px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white flex items-center gap-1.5">
                                <Truck className="w-3 h-3" /> Assign Delivery
                              </button>
                            )}
                            {subStatus === 'delivered' && (
                              <span className="text-[11px] text-emerald-600 font-bold flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" /> Delivered
                                {sub.delivery_date && <span className="text-slate-400 font-normal"> · {format(new Date(sub.delivery_date), 'dd MMM, h:mm a')}</span>}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Pieces & Verification Photos — unified card */}
              <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                {/* Header */}
                <div className="bg-slate-50 border-b border-slate-200 px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Camera className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-bold text-slate-700">Pieces & Verification Photos</span>
                  </div>
                  {selectedOrder.total_pieces > 0 && (
                    <span className={`text-xs font-black px-2.5 py-1 rounded-full ${
                      (selectedOrder.photos?.length || 0) >= selectedOrder.total_pieces
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      {selectedOrder.photos?.length || 0} / {selectedOrder.total_pieces} photos
                    </span>
                  )}
                </div>

                <div className="p-4 space-y-4">
                  {/* Total pieces input */}
                  <div>
                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1.5">
                      Total Pieces <span className="text-red-500">*</span>
                      {!selectedOrder.total_pieces && (
                        <span className="ml-2 text-[10px] font-semibold text-red-500 normal-case tracking-normal">Required before Inspection Complete</span>
                      )}
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="number" min="1" step="1"
                        value={piecesInput}
                        onChange={e => setPiecesInput(e.target.value)}
                        placeholder={selectedOrder.total_pieces ? `Currently: ${selectedOrder.total_pieces} pcs` : 'e.g. 10'}
                        className="flex-1 px-3 py-2 border border-slate-200 rounded-xl text-sm font-bold text-slate-800 outline-none focus:border-blue-500 bg-slate-50"
                      />
                      <button onClick={handleSetPieces} disabled={settingPieces || !piecesInput}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold rounded-xl disabled:opacity-60 flex items-center gap-1.5 whitespace-nowrap">
                        {settingPieces ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                        {selectedOrder.total_pieces ? 'Update' : 'Set Pieces'}
                      </button>
                    </div>
                    {selectedOrder.total_pieces > 0 && (
                      <p className="text-xs mt-1.5">
                        {(selectedOrder.photos?.length || 0) >= selectedOrder.total_pieces
                          ? <span className="text-emerald-600 font-semibold">✓ All {selectedOrder.total_pieces} photos uploaded — ready for Inspection Complete</span>
                          : <span className="text-amber-600">Upload <strong>{selectedOrder.total_pieces} photos</strong> (one per piece) — <strong>{selectedOrder.total_pieces - (selectedOrder.photos?.length || 0)} more needed</strong></span>
                        }
                      </p>
                    )}
                  </div>

                  {/* Upload button */}
                  <div>
                    <input ref={fileInputRef} type="file" multiple accept="image/*" onChange={handlePhotoUpload} className="hidden" />
                    <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                      className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 border-dashed text-sm font-semibold transition-all disabled:opacity-60 ${
                        selectedOrder.total_pieces > 0 && (selectedOrder.photos?.length || 0) >= selectedOrder.total_pieces
                          ? 'border-emerald-300 text-emerald-600 hover:border-emerald-400 bg-emerald-50/40'
                          : 'border-blue-300 text-blue-600 hover:bg-blue-50'
                      }`}>
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                      {uploading ? 'Uploading...'
                        : selectedOrder.total_pieces > 0
                          ? `Click to select all ${selectedOrder.total_pieces} photos at once`
                          : 'Upload Photos (select multiple at once)'}
                    </button>
                  </div>

                  {/* Photo grid — shown inside the card */}
                  {selectedOrder.photos?.length > 0 && (
                    <div>
                      <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                        Uploaded ({selectedOrder.photos.length})
                      </p>
                      <div className="grid grid-cols-5 gap-2">
                        {selectedOrder.photos.map((photo, idx) => (
                          <div key={idx} className="relative group aspect-square rounded-lg overflow-hidden border border-slate-200">
                            {/* View on click */}
                            <button
                              onClick={() => { setSelectedPhoto(photo); setShowPhotoModal(true); }}
                              className="w-full h-full">
                              <img
                                src={`${BACKEND_URL}${photo.url}`}
                                alt={`Photo ${idx + 1}`}
                                className="w-full h-full object-cover group-hover:opacity-70 transition-opacity"
                              />
                            </button>
                            {/* Number badge */}
                            <span className="absolute bottom-0 left-0 right-0 bg-black/40 text-white text-[9px] font-bold text-center py-0.5 pointer-events-none">
                              {idx + 1}
                            </span>
                            {/* Delete button — appears on hover */}
                            <button
                              onClick={() => handleDeletePhoto(idx)}
                              disabled={deletingPhoto === idx}
                              className="absolute top-0.5 right-0.5 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-60 z-10">
                              {deletingPhoto === idx
                                ? <Loader2 className="w-2.5 h-2.5 animate-spin" />
                                : <X className="w-2.5 h-2.5" />}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment info moved to separate Payment modal — open via Payment button in table */}

              {/* Cancel Order — shown for non-delivered, non-cancelled orders */}
              {!['delivered', 'cancelled'].includes(selectedOrder.status) && (
                <div className="pt-2">
                  <button
                    onClick={() => setShowCancelModal(true)}
                    className="w-full py-2.5 rounded-2xl bg-red-50 hover:bg-red-100 border border-red-200 text-red-600 font-bold text-sm transition-all flex items-center justify-center gap-2">
                    <XCircle className="w-4 h-4" /> Cancel This Order
                  </button>
                </div>
              )}

              {/* Issue display */}
              {selectedOrder.issue && (
                <div className={`p-4 rounded-2xl border ${selectedOrder.issue.status === 'open' ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle className={`w-4 h-4 ${selectedOrder.issue.status === 'open' ? 'text-amber-600' : 'text-emerald-600'}`} />
                    <span className={`text-sm font-bold ${selectedOrder.issue.status === 'open' ? 'text-amber-800' : 'text-emerald-800'}`}>
                      Issue: {selectedOrder.issue.issue_type}
                    </span>
                    <span className={`ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full ${selectedOrder.issue.status === 'open' ? 'bg-amber-200 text-amber-800' : 'bg-emerald-200 text-emerald-800'}`}>
                      {selectedOrder.issue.status}
                    </span>
                  </div>
                  {selectedOrder.issue.description && <p className="text-xs text-slate-600">{selectedOrder.issue.description}</p>}
                  {selectedOrder.issue.resolution && <p className="text-xs text-emerald-700 mt-1 font-medium">Resolution: {selectedOrder.issue.resolution}</p>}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Photo viewer */}
      <Dialog open={showPhotoModal} onOpenChange={setShowPhotoModal}>
        <DialogContent className="max-w-lg p-2 rounded-3xl">
          {selectedPhoto && (
            <img src={`${BACKEND_URL}${selectedPhoto.url}`} alt="" className="w-full rounded-2xl" />
          )}
        </DialogContent>
      </Dialog>

      {/* Issue resolution modal */}
      <Dialog open={showIssueModal} onOpenChange={setShowIssueModal}>
        <DialogContent className="max-w-sm rounded-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <AlertTriangle className="w-5 h-5 text-amber-600" /> Resolve Issue
            </DialogTitle>
          </DialogHeader>
          {selectedOrder?.issue && (
            <div className="space-y-3 py-2">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                <p className="text-xs font-bold text-amber-700 mb-1">{selectedOrder.issue.issue_type}</p>
                <p className="text-xs text-slate-600">{selectedOrder.issue.description}</p>
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Resolution Note</label>
                <Textarea
                  value={issueResolution}
                  onChange={e => setIssueResolution(e.target.value)}
                  placeholder="Describe how the issue was resolved..."
                  className="min-h-[80px] rounded-xl border-slate-200 bg-slate-50"
                />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <button onClick={() => setShowIssueModal(false)}
              className="flex-1 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50">
              Cancel
            </button>
            <button onClick={handleResolveIssue}
              className="flex-1 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-sm">
              Mark Resolved
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Payment & Discount Modal ─────────────────────────────────────────── */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto rounded-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <IndianRupee className="w-5 h-5 text-emerald-600" /> Payment & Discounts
            </DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4 py-1">

              {/* Status badge */}
              <div className={`flex items-center justify-between px-4 py-3 rounded-2xl ${selectedOrder.payment_status === 'paid' ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="flex items-center gap-2">
                  {selectedOrder.payment_status === 'paid'
                    ? <CheckCircle className="w-5 h-5 text-emerald-600" />
                    : <XCircle className="w-5 h-5 text-red-500" />}
                  <span className="font-bold text-slate-800 text-sm">
                    {selectedOrder.payment_status === 'paid' ? 'Payment Received' : 'Payment Pending'}
                  </span>
                </div>
                <span className={`text-xs font-black px-2.5 py-1 rounded-full ${selectedOrder.payment_status === 'paid' ? 'bg-emerald-200 text-emerald-800' : 'bg-red-200 text-red-700'}`}>
                  ₹{selectedOrder.total_amount?.toLocaleString('en-IN')}
                </span>
              </div>

              {/* Per-item breakdown with discounts */}
              {selectedOrder.cart_items?.length > 0 && (
                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4">
                  <p className="text-xs font-black text-slate-500 uppercase tracking-widest mb-3">Items & Discounts</p>
                  <div className="space-y-2">
                    {selectedOrder.cart_items.map((item, i) => (
                      <div key={i} className="bg-white rounded-xl px-3 py-2.5 border border-slate-100">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-slate-700 truncate">{item.description}</p>
                            <p className="text-[10px] text-slate-400 mt-0.5">{item.quantity} {item.unit} × ₹{item.price_per_unit}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            {(item.discount_pct || 0) > 0 ? (
                              <div className="space-y-0.5">
                                <p className="text-[10px] line-through text-slate-400">₹{item.total}</p>
                                <p className="text-[10px] font-bold text-orange-600">−{item.discount_pct}% off</p>
                                <p className="text-xs font-black text-emerald-700">₹{item.final_total ?? item.total}</p>
                              </div>
                            ) : (
                              <p className="text-xs font-bold text-slate-700">₹{item.total}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Overall Discount */}
              <div className="bg-orange-50 border border-orange-100 rounded-2xl p-4">
                <p className="text-xs font-black text-slate-500 uppercase tracking-widest mb-3">Overall Order Discount</p>
                {(selectedOrder.overall_discount_pct || 0) > 0 && (
                  <div className="flex items-center justify-between bg-white border border-orange-200 rounded-xl px-3 py-2 mb-3">
                    <span className="text-xs font-semibold text-slate-600">Current discount</span>
                    <div className="text-right">
                      <span className="text-sm font-black text-orange-600">{selectedOrder.overall_discount_pct}% off</span>
                      <span className="text-[10px] text-slate-400 block">−₹{selectedOrder.overall_discount_amount?.toFixed(2)}</span>
                    </div>
                  </div>
                )}
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <input
                      type="number" min="0" max="100" step="0.5"
                      value={overallDiscountInput}
                      onChange={e => setOverallDiscountInput(e.target.value)}
                      placeholder={selectedOrder.overall_discount_pct > 0 ? `Current: ${selectedOrder.overall_discount_pct}%` : 'e.g. 10'}
                      className="w-full pl-3 pr-8 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-slate-800 focus:outline-none focus:border-orange-400 transition-colors"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-bold">%</span>
                  </div>
                  <button
                    onClick={handleApplyOverallDiscount}
                    disabled={savingOverallDiscount || overallDiscountInput === ''}
                    className="px-4 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-bold text-sm transition-all flex items-center gap-1.5">
                    {savingOverallDiscount ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Percent className="w-3.5 h-3.5" />}
                    Apply
                  </button>
                  {(selectedOrder.overall_discount_pct || 0) > 0 && (
                    <button
                      onClick={async () => {
                        setSavingOverallDiscount(true);
                        try {
                          const res = await axios.patch(
                            `${API}/admin/individual-orders/${selectedOrder.id}/overall-discount`,
                            { discount_pct: 0 },
                            { headers: { Authorization: `Bearer ${token}` } }
                          );
                          toast.success('Overall discount removed');
                          setSelectedOrder(res.data);
                          fetchOrders();
                          setOverallDiscountInput('');
                        } catch (err) {
                          toast.error('Failed to remove discount');
                        } finally { setSavingOverallDiscount(false); }
                      }}
                      disabled={savingOverallDiscount}
                      className="px-3 py-2.5 rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-100 text-xs font-semibold transition-all disabled:opacity-50">
                      Remove
                    </button>
                  )}
                </div>
                <p className="text-[10px] text-slate-400 mt-2">Applied on top of all other discounts · Enter 0 to remove</p>
              </div>

              {/* Price breakdown */}
              <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-2 text-xs">
                <p className="text-xs font-black text-slate-500 uppercase tracking-widest mb-1">Price Breakdown</p>
                {selectedOrder.subtotal != null && (
                  <div className="flex justify-between text-slate-600">
                    <span>Subtotal (pre-tax)</span>
                    <span className="font-medium">₹{selectedOrder.subtotal?.toFixed(2)}</span>
                  </div>
                )}
                {selectedOrder.cart_items?.some(i => (i.discount_pct || 0) > 0) && (
                  <div className="flex justify-between text-orange-600">
                    <span>Item Discounts</span>
                    <span className="font-bold">
                      −₹{selectedOrder.cart_items.reduce((s, i) => s + (i.discount_amount || 0), 0).toFixed(2)}
                    </span>
                  </div>
                )}
                {selectedOrder.gst_total > 0 && (
                  <>
                    <div className="flex justify-between text-amber-700">
                      <span>CGST (9%)</span>
                      <span>₹{selectedOrder.cgst_total?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-amber-700">
                      <span>SGST (9%)</span>
                      <span>₹{selectedOrder.sgst_total?.toFixed(2)}</span>
                    </div>
                  </>
                )}
                {selectedOrder.coupon_discount > 0 && (
                  <div className="flex justify-between text-emerald-700">
                    <span>Coupon {selectedOrder.coupon_code ? `(${selectedOrder.coupon_code})` : 'Discount'}</span>
                    <span className="font-bold">−₹{selectedOrder.coupon_discount?.toFixed(2)}</span>
                  </div>
                )}
                {selectedOrder.referral_discount > 0 && (
                  <div className="flex justify-between text-violet-600">
                    <span>{selectedOrder.referral_type === 'referrer_reward' ? 'Referral Reward (5%)' : 'Welcome Discount (10%)'}</span>
                    <span className="font-bold">−₹{selectedOrder.referral_discount?.toFixed(2)}</span>
                  </div>
                )}
                {(selectedOrder.overall_discount_amount || 0) > 0 && (
                  <div className="flex justify-between text-orange-600 font-bold">
                    <span>Overall Discount ({selectedOrder.overall_discount_pct}%)</span>
                    <span>−₹{selectedOrder.overall_discount_amount?.toFixed(2)}</span>
                  </div>
                )}
                {selectedOrder.delivery_charge > 0 && (
                  <div className="flex justify-between text-slate-600">
                    <span>Delivery Charge</span>
                    <span>₹{selectedOrder.delivery_charge?.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between border-t border-slate-200 pt-2 mt-1">
                  <span className="font-black text-slate-800">Grand Total</span>
                  <span className="font-black text-slate-900 text-sm">₹{selectedOrder.total_amount?.toFixed(2)}</span>
                </div>
              </div>

              {/* Transaction info */}
              {(selectedOrder.payment_method || selectedOrder.paid_at || selectedOrder.payment_txn_id || selectedOrder.invoice_number) && (
                <div className="bg-slate-50 border border-slate-100 rounded-2xl p-4 space-y-1.5 text-xs text-slate-600">
                  <p className="text-xs font-black text-slate-500 uppercase tracking-widest mb-1">Transaction Info</p>
                  {selectedOrder.invoice_number && (
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Invoice No.</span>
                      <span className="font-mono text-[11px] bg-blue-50 text-blue-700 border border-blue-200 rounded px-1.5 py-0.5">{selectedOrder.invoice_number}</span>
                    </div>
                  )}
                  {selectedOrder.payment_method && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Method</span>
                      <span className="font-semibold capitalize">
                        {selectedOrder.payment_method === 'easebuzz' ? 'Easebuzz (Online)' : selectedOrder.payment_method === 'upi' ? 'UPI / Bank Transfer' : 'Cash'}
                      </span>
                    </div>
                  )}
                  {selectedOrder.paid_at && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Paid at</span>
                      <span className="font-medium">{format(new Date(selectedOrder.paid_at), 'dd MMM yyyy, h:mm a')}</span>
                    </div>
                  )}
                  {selectedOrder.payment_txn_id && (
                    <div className="flex justify-between items-start gap-2">
                      <span className="text-slate-400 flex-shrink-0">Txn ID</span>
                      <span className="font-mono text-[10px] break-all text-right">{selectedOrder.payment_txn_id}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Admin actions */}
              <div className="space-y-2">
                {selectedOrder.payment_status !== 'paid' && selectedOrder.payment_method !== 'easebuzz' && (
                  <>
                    <button
                      onClick={() => handleUpdatePayment('paid', 'cash')}
                      disabled={updatingPayment}
                      className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 rounded-xl text-sm transition-all disabled:opacity-60">
                      {updatingPayment ? <Loader2 className="w-4 h-4 animate-spin" /> : <IndianRupee className="w-4 h-4" />}
                      Mark as Paid (Cash)
                    </button>
                    <button
                      onClick={() => handleUpdatePayment('paid', 'upi')}
                      disabled={updatingPayment}
                      className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-xl text-sm transition-all disabled:opacity-60">
                      {updatingPayment ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                      Paid via UPI / Bank Transfer
                    </button>
                  </>
                )}
                {selectedOrder.payment_status === 'paid' && selectedOrder.payment_method !== 'easebuzz' && (
                  <button
                    onClick={() => handleUpdatePayment('unpaid', 'cash')}
                    disabled={updatingPayment}
                    className="w-full flex items-center justify-center gap-2 border border-red-200 text-red-600 hover:bg-red-50 font-semibold py-2.5 rounded-xl text-sm transition-all disabled:opacity-60">
                    {updatingPayment ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    Mark as Unpaid
                  </button>
                )}
                {selectedOrder.payment_status === 'paid' && selectedOrder.payment_method === 'easebuzz' && (
                  <p className="text-xs text-emerald-600 text-center font-medium bg-emerald-50 border border-emerald-200 rounded-xl py-2.5">
                    Paid via Easebuzz — cannot be manually overridden
                  </p>
                )}
                <button
                  onClick={() => downloadInvoice(selectedOrder)}
                  className="w-full flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-2.5 rounded-xl text-sm transition-all border border-slate-200 mt-1">
                  <Download className="w-4 h-4" />
                  Download Invoice PDF
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Order Modal */}
      <CreateOrderModal
        show={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        token={token}
        onSuccess={() => { setShowCreateModal(false); fetchOrders(); }}
        serviceCategories={liveServiceCategories}
        catalogItems={liveCatalogItems}
      />

      {/* Assign Delivery Boy Modal */}
      <Dialog open={showAssignModal} onOpenChange={(open) => { setShowAssignModal(open); if (!open) setAssignSubType(null); }}>
        <DialogContent className="max-w-sm rounded-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Truck className={`w-5 h-5 ${assignType === 'pickup' ? 'text-blue-500' : 'text-emerald-500'}`} />
              {assignSubType
                ? `Assign ${assignType === 'pickup' ? 'Pickup' : 'Delivery'} — ${assignSubType === 'express' ? 'Express' : 'Standard'} Sub-order`
                : assignType === 'pickup' ? 'Assign Pickup Boy' : 'Assign Delivery Boy'}
            </DialogTitle>
          </DialogHeader>
          {assignOrder && (
            <div className="space-y-4 py-1">
              <div className="bg-slate-50 rounded-xl px-4 py-3 text-sm">
                <p className="font-semibold text-slate-800">{assignOrder.customer_name}</p>
                <p className="text-xs text-slate-500 mt-0.5">{assignOrder.address_line2 ? `${assignOrder.address_line2}, ` : ''}{assignOrder.address_line1}</p>
                <p className="text-xs text-orange-600 font-medium mt-1">{assignOrder.total_clothes > 0 ? `${assignOrder.total_clothes} items` : ''}{assignOrder.actual_kg ? ` · ${assignOrder.actual_kg}kg` : ''}</p>
                {assignOrder.pickup_date && (
                  <p className="text-xs text-blue-600 font-medium mt-1">
                    📅 {assignOrder.pickup_date}{assignOrder.pickup_time_slot ? ` · ${assignOrder.pickup_time_slot}` : ''}
                  </p>
                )}
                {assignSubType && (
                  <span className={`inline-block mt-1.5 text-[10px] font-bold px-2 py-0.5 rounded-full ${assignSubType === 'express' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                    {assignSubType === 'express' ? '⚡ Express' : '📦 Standard'} sub-order
                  </span>
                )}
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1.5">Select Delivery Boy</label>
                {loadingBoys ? (
                  <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-emerald-500" /></div>
                ) : deliveryBoys.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-3">No delivery staff created yet</p>
                ) : (
                  <div className="border border-slate-200 rounded-xl overflow-hidden max-h-40 overflow-y-auto">
                    {deliveryBoys.map(boy => (
                      <button key={boy.id}
                        onClick={() => setAssignForm(f => ({ ...f, delivery_boy_id: boy.id }))}
                        className={`w-full px-4 py-2.5 flex items-center gap-3 text-left border-b border-slate-100 last:border-0 transition-colors ${assignForm.delivery_boy_id === boy.id ? 'bg-emerald-50 border-l-4 border-l-emerald-500' : 'hover:bg-slate-50'}`}>
                        <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-emerald-700">{boy.name?.[0]?.toUpperCase()}</span>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-700">{boy.name}</p>
                          <p className="text-xs text-slate-400">{boy.pending_count || 0} active tasks</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1.5">Scheduled Date</label>
                <input type="date" value={assignForm.scheduled_date}
                  onChange={e => setAssignForm(f => ({ ...f, scheduled_date: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-400" />
              </div>

              {assignType === 'pickup' && (
                <div>
                  <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1.5">
                    Pickup Time Slot
                    {assignOrder?.pickup_time_slot && (
                      <span className="ml-1.5 font-normal text-blue-600 normal-case">· Customer chose: {assignOrder.pickup_time_slot}</span>
                    )}
                  </label>
                  <div className="grid grid-cols-2 gap-1.5">
                    {PICKUP_TIME_SLOTS.map(slot => (
                      <button key={slot} type="button"
                        onClick={() => setAssignForm(f => ({ ...f, scheduled_time: f.scheduled_time === slot ? '' : slot }))}
                        className={`px-2 py-2 rounded-xl border text-xs font-semibold transition-all ${
                          assignForm.scheduled_time === slot
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-slate-50 text-slate-600 border-slate-200 hover:border-blue-300'
                        }`}
                      >
                        {slot}
                      </button>
                    ))}
                  </div>
                  {assignForm.scheduled_time && (
                    <p className="text-xs text-blue-600 font-medium mt-1.5">
                      ✓ Pickup on {assignForm.scheduled_date} at {assignForm.scheduled_time}
                    </p>
                  )}
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1.5">Notes <span className="font-normal text-slate-400 normal-case">(optional)</span></label>
                <textarea value={assignForm.notes}
                  onChange={e => setAssignForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Any instructions for the delivery boy..."
                  rows={2}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm resize-none focus:outline-none focus:border-emerald-400" />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <button onClick={() => { setShowAssignModal(false); setAssignSubType(null); }} className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
            <button
              onClick={assignSubType ? handleSubOrderAssign : handleAssignDelivery}
              disabled={assigning || !assignForm.delivery_boy_id}
              className={`px-5 py-2 rounded-xl text-white text-sm font-bold disabled:opacity-50 flex items-center gap-2 ${assignType === 'pickup' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-emerald-600 hover:bg-emerald-700'}`}>
              {assigning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4" />}
              {assignSubType ? `Assign ${assignType === 'pickup' ? 'Pickup' : 'Delivery'}` : assignType === 'pickup' ? 'Assign Pickup' : 'Assign Delivery'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Cancel Order Confirmation Modal ─────────────────────────────────── */}
      <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
        <DialogContent className="max-w-sm rounded-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-black text-red-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <XCircle className="w-5 h-5" /> Cancel Order
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-slate-600">
              Are you sure you want to cancel this order for <span className="font-bold">{selectedOrder?.customer_name}</span>? The customer will be notified.
            </p>
            <div>
              <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Reason (optional)</label>
              <Textarea
                value={cancelReason}
                onChange={e => setCancelReason(e.target.value)}
                placeholder="e.g. Customer requested, item not available..."
                className="min-h-[70px] rounded-xl border-slate-200 bg-slate-50"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <button onClick={() => { setShowCancelModal(false); setCancelReason(''); }}
              className="flex-1 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50">
              Go Back
            </button>
            <button onClick={handleCancelOrder} disabled={cancelling}
              className="flex-1 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white font-bold text-sm flex items-center justify-center gap-1.5">
              {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
              Cancel Order
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      </> /* end activeTab === 'orders' */}
    </div>
  );
};

// ── Referrals Panel ────────────────────────────────────────────────────────────
const REFERRAL_STATUS = {
  registered:         { label: 'Registered',        color: 'bg-amber-100 text-amber-700',    icon: Clock },
  first_order_placed: { label: 'First Order Placed', color: 'bg-blue-100 text-blue-700',     icon: CheckCircle },
  reward_applied:     { label: 'Reward Applied',     color: 'bg-emerald-100 text-emerald-700', icon: Gift },
};

const ReferralsPanel = ({ referrals, stats, loading, filter, setFilter, onRefresh }) => {
  const filtered = filter === 'all' ? referrals : referrals.filter(r => r.status === filter);

  return (
    <div className="space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Referrals', value: stats?.total_count ?? referrals.length, color: 'bg-orange-50 text-orange-600', icon: Gift },
          { label: 'Active Referrers', value: stats?.active_referrers ?? 0, color: 'bg-blue-50 text-blue-600', icon: Users },
          { label: 'Reward Applied', value: referrals.filter(r => r.status === 'reward_applied').length, color: 'bg-emerald-50 text-emerald-600', icon: CheckCircle },
          { label: 'Total Discount Given', value: `₹${(stats?.total_discount_given ?? 0).toFixed(0)}`, color: 'bg-violet-50 text-violet-600', icon: IndianRupee },
        ].map(({ label, value, color, icon: Icon }) => (
          <div key={label} className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 ${color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                <Icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xl font-black text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{value}</p>
                <p className="text-xs text-slate-500 font-semibold">{label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filter pills + refresh */}
      <div className="flex items-center gap-2 flex-wrap">
        {['all', 'registered', 'first_order_placed', 'reward_applied'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all ${filter === f ? 'bg-slate-800 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
            {f === 'all' ? 'All' : REFERRAL_STATUS[f]?.label}
          </button>
        ))}
        <button onClick={onRefresh} className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white border border-slate-200 text-slate-500 text-xs font-semibold hover:bg-slate-50 transition-all">
          <Loader2 className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl py-16 flex flex-col items-center text-center">
          <Gift className="w-12 h-12 text-slate-300 mb-3" strokeWidth={1.5} />
          <p className="font-semibold text-slate-500 mb-1">No referrals yet</p>
          <p className="text-sm text-slate-400">Referral chains will appear here once customers share their codes</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-4 py-3 font-black text-slate-500 text-xs uppercase tracking-wider">Referrer</th>
                  <th className="text-left px-4 py-3 font-black text-slate-500 text-xs uppercase tracking-wider"></th>
                  <th className="text-left px-4 py-3 font-black text-slate-500 text-xs uppercase tracking-wider">Joined Friend</th>
                  <th className="text-left px-4 py-3 font-black text-slate-500 text-xs uppercase tracking-wider">Status</th>
                  <th className="text-left px-4 py-3 font-black text-slate-500 text-xs uppercase tracking-wider">Registered</th>
                  <th className="text-left px-4 py-3 font-black text-slate-500 text-xs uppercase tracking-wider">First Order</th>
                  <th className="text-left px-4 py-3 font-black text-slate-500 text-xs uppercase tracking-wider">Discounts</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((r, i) => {
                  const cfg = REFERRAL_STATUS[r.status] || REFERRAL_STATUS.registered;
                  const Icon = cfg.icon;
                  return (
                    <tr key={i} className="hover:bg-slate-50/60 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-blue-100 rounded-xl flex items-center justify-center font-black text-blue-600 text-xs flex-shrink-0">
                            {(r.referrer_name || '?')[0].toUpperCase()}
                          </div>
                          <div>
                            <p className="font-semibold text-slate-800 text-xs">{r.referrer_name || '—'}</p>
                            {r.referrer_phone && <p className="text-[10px] text-slate-400">{r.referrer_phone}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-1 py-3">
                        <ArrowRight className="w-4 h-4 text-slate-300" />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-orange-100 rounded-xl flex items-center justify-center font-black text-orange-600 text-xs flex-shrink-0">
                            {(r.referee_name || '?')[0].toUpperCase()}
                          </div>
                          <div>
                            <p className="font-semibold text-slate-800 text-xs">{r.referee_name || '—'}</p>
                            {r.referee_phone && <p className="text-[10px] text-slate-400">{r.referee_phone}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold ${cfg.color}`}>
                          <Icon className="w-2.5 h-2.5" />{cfg.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        {r.registered_at ? new Date(r.registered_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' }) : '—'}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        {r.first_order_at ? new Date(r.first_order_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' }) : <span className="text-slate-300">Pending</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-[10px] font-black bg-violet-50 text-violet-600 border border-violet-100 px-2 py-0.5 rounded-full">
                            10% → friend
                          </span>
                          <span className={`text-[10px] font-black px-2 py-0.5 rounded-full border ${r.status === 'reward_applied' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-slate-50 text-slate-400 border-slate-200'}`}>
                            5% → referrer {r.status === 'reward_applied' ? '✓' : '(pending)'}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 border-t border-slate-100 bg-slate-50">
            <p className="text-xs text-slate-400">{filtered.length} referral{filtered.length !== 1 ? 's' : ''} {filter !== 'all' ? `· filtered by ${REFERRAL_STATUS[filter]?.label}` : ''}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminIndividualOrders;
